/**
 * @fileoverview Página responsável pela comunicação interna entre usuários da plataforma.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress, useMediaQuery, Snackbar, Menu
} from "@material-ui/core";
import EmojiEmotionsIcon from "@material-ui/icons/EmojiEmotions";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import TagIcon from "@material-ui/icons/LocalOffer";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import RotatableImage from "../../components/RotatableImage";
import SelectJobRoleDialog from "../../components/SelectJobRoleDialog";

import { ChevronDown, Plus, Search, X, Send, ArrowLeft, Users, Trash2, Paperclip, File, Download, Pencil, MoreVertical, Star, BellOff, Bell, CheckCheck, Hash, Pin, Bookmark, AtSign, Smile, Mail, UserPlus, Clock } from "lucide-react";
/* ═══════════════════════════════════════════════════════════
   STTELAR DESIGN SYSTEM — Internal Chat
   Royal blue accent (#3D5CFD), zinc-dark backgrounds,
   frosted glass sidebar, refined micro-interactions.
   ═══════════════════════════════════════════════════════════ */

// Paleta UNIFICADA com o resto da app (LeadDetailModal/KanbanCard usam a mesma)
const AVATAR_COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#6366f1", "#f43f5e"];

const stringToColor = (str) => {
  if (!str) return "#9e9e9e";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Manter API antiga retornando [color, color] pra não quebrar quem usa gradient
const getGradient = (name) => {
  const c = stringToColor(name);
  return [c, c];
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";

  // Sttelar palette
  const accent = dark ? "#5B7CFF" : "#3D5CFD";
  const accentSoft = dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)";
  const accentGlow = dark ? "rgba(91,124,255,0.25)" : "rgba(61,92,253,0.15)";
  const bg0 = dark ? "#0A0A0A" : "#fafafa";
  const bg2 = dark ? "#1E1E22" : "#f4f4f5";
  const border = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const borderStrong = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const text0 = dark ? "#FAFAFA" : "#09090B";
  const text2 = dark ? "#71717A" : "#A1A1AA";
  const glassStrong = dark
    ? "rgba(24,24,27,0.95)"
    : "rgba(255,255,255,0.97)";

  return {
    "@global": {
      "@keyframes sttelarSlideIn": {
        from: { opacity: 0, transform: "translateY(4px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "@keyframes sttelarFadeIn": {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      "@keyframes sttelarPulse": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.6 },
      },
    },

    root: {
      display: "flex",
      height: "100%",
      overflow: "hidden",
      background: bg0,
    },

    // ═══ SIDEBAR ═══
    sidebar: {
      width: 288,
      minWidth: 288,
      display: "flex",
      flexDirection: "column",
      background: dark
        ? "linear-gradient(180deg, rgba(24,24,27,0.98) 0%, rgba(10,10,10,0.98) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
      borderRight: `1px solid ${border}`,
      position: "relative",
      zIndex: 2,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: "100%",
      },
      // Subtle glow line at the right edge
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 1,
        background: dark
          ? "linear-gradient(180deg, transparent, rgba(91,124,255,0.1), transparent)"
          : "transparent",
      },
    },
    sidebarHeader: {
      padding: "20px 20px 12px",
      flexShrink: 0,
    },
    teamName: {
      fontSize: 18,
      fontWeight: 800,
      color: text0,
      letterSpacing: "-0.4px",
      lineHeight: 1.2,
    },
    teamSub: {
      fontSize: 11,
      color: text2,
      marginTop: 2,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    sidebarSearch: {
      padding: "0 12px 8px",
      flexShrink: 0,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
        borderRadius: 10,
        fontSize: 13,
        transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
        "& fieldset": { border: `1px solid ${border}` },
        "&:hover": {
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          "& fieldset": { borderColor: borderStrong },
        },
        "&.Mui-focused": {
          background: dark ? "rgba(255,255,255,0.06)" : "#fff",
          "& fieldset": { borderColor: `${accent} !important`, borderWidth: "1px !important" },
          boxShadow: `0 0 0 3px ${accentSoft}`,
        },
      },
    },
    channelList: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      padding: "4px 8px",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        borderRadius: 2,
      },
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      padding: "10px 12px 4px",
      cursor: "pointer",
      userSelect: "none",
      borderRadius: 6,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      },
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      color: text2,
      flex: 1,
    },
    sectionToggle: {
      fontSize: 16,
      color: text2,
      marginRight: 6,
      transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
    },
    channelMenuBtn: {
      opacity: 0,
      padding: 2,
      color: "inherit",
      "$channelItem:hover &": {
        opacity: 0.6,
      },
      "&:hover": {
        opacity: 1,
      },
    },
    channelItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "7px 12px",
      borderRadius: 8,
      cursor: "pointer",
      fontSize: 14,
      color: dark ? "#A1A1AA" : "#71717A",
      fontWeight: 500,
      transition: "all 0.15s cubic-bezier(0.4,0,0.2,1)",
      position: "relative",
      marginBottom: 1,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        color: text0,
      },
    },
    channelItemActive: {
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      color: dark ? "#5B7CFF" : "#3D5CFD",
      fontWeight: 600,
      "&:hover": {
        background: accentGlow,
      },
      // Left accent bar
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 6,
        bottom: 6,
        width: 3,
        borderRadius: 2,
        background: accent,
      },
    },
    channelItemUnread: {
      color: text0,
      fontWeight: 700,
      background: dark ? "rgba(91,124,255,0.06)" : "rgba(61,92,253,0.04)",
    },
    unreadDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: accent,
      flexShrink: 0,
      animation: "$sttelarPulse 2s ease-in-out infinite",
      boxShadow: `0 0 6px ${accent}60`,
    },
    mentionAtBadge: {
      width: 18,
      height: 18,
      borderRadius: "50%",
      background: accent,
      color: "#fff",
      fontSize: 11,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      animation: "$sttelarPulse 2s ease-in-out infinite",
      boxShadow: `0 0 8px ${accent}60`,
      fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
    },
    channelPreview: {
      fontSize: 11,
      color: text2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: "16px",
      marginTop: 1,
    },
    channelPreviewUnread: {
      color: dark ? "#A1A1AA" : "#71717A",
      fontWeight: 500,
    },
    channelHash: {
      fontSize: 15,
      fontWeight: 800,
      opacity: 0.4,
      width: 20,
      textAlign: "center",
      flexShrink: 0,
      fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
    },
    channelName: {
      display: "block",
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: "20px",
      minWidth: 0,
    },
    unreadBadge: {
      background: accent,
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 7px",
      borderRadius: 10,
      minWidth: 18,
      textAlign: "center",
      lineHeight: "16px",
      letterSpacing: "-0.2px",
    },
    dmAvatar: {
      width: 24,
      height: 24,
      borderRadius: "50%",
      fontSize: 9,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
    },
    dmAvatarWrapper: {
      position: "relative",
      flexShrink: 0,
      display: "flex",
    },
    statusDot: {
      position: "absolute",
      bottom: -1,
      right: -1,
      width: 8,
      height: 8,
      borderRadius: "50%",
      border: `1.5px solid ${dark ? "#18181B" : "#ffffff"}`,
    },
    statusDotOnline: {
      background: "#22C55E",
    },
    statusDotOffline: {
      background: "#EF4444",
    },
    memberAvatarWrapper: {
      position: "relative",
      flexShrink: 0,
      display: "flex",
    },
    memberStatusDot: {
      position: "absolute",
      bottom: -1,
      right: -1,
      width: 9,
      height: 9,
      borderRadius: "50%",
      border: `1.5px solid ${dark ? "#18181B" : "#ffffff"}`,
    },
    addChannelBtn: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 8,
      cursor: "pointer",
      color: text2,
      fontSize: 13,
      fontWeight: 500,
      transition: "all 0.15s",
      "&:hover": {
        color: dark ? "#5B7CFF" : "#3D5CFD",
        background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      },
    },

    // ═══ CHAT AREA ═══
    chatArea: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      position: "relative",
      background: bg0,
    },
    chatHeader: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 24px",
      background: glassStrong,
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
      minHeight: 56,
      zIndex: 1,
      "&:hover $deleteChannelBtn": {
        color: text2,
      },
    },
    chatHeaderName: {
      fontSize: 16,
      fontWeight: 700,
      color: text0,
      letterSpacing: "-0.2px",
    },
    chatHeaderHash: {
      fontSize: 16,
      fontWeight: 800,
      color: text2,
      marginRight: -4,
      fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
    },
    chatHeaderMeta: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      color: text2,
      cursor: "pointer",
      padding: "4px 10px",
      borderRadius: 6,
      border: `1px solid ${border}`,
      transition: "all 0.15s",
      "&:hover": {
        borderColor: borderStrong,
        color: dark ? "#A1A1AA" : "#71717A",
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      },
    },
    chatHeaderActions: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginLeft: "auto",
    },
    chatHeaderBtn: {
      padding: 7,
      borderRadius: 8,
      color: text2,
      transition: "all 0.15s",
      "&:hover": {
        color: text0,
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      },
    },
    deleteChannelBtn: {
      padding: 7,
      borderRadius: 8,
      color: "transparent",
      transition: "all 0.2s",
      "&:hover": {
        color: "#EF4444",
        background: "rgba(239,68,68,0.08)",
      },
    },

    // ═══ MESSAGES ═══
    messageList: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      padding: "8px 0",
      "&::-webkit-scrollbar": { width: 5 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        borderRadius: 3,
        "&:hover": {
          background: dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
        },
      },
    },
    dateSep: {
      display: "flex",
      alignItems: "center",
      padding: "20px 24px 10px",
      gap: 16,
      "&::before, &::after": {
        content: '""',
        flex: 1,
        height: 1,
        background: border,
      },
    },
    dateSepText: {
      fontSize: 11,
      fontWeight: 700,
      color: text2,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      whiteSpace: "nowrap",
    },
    unreadSep: {
      display: "flex",
      alignItems: "center",
      padding: "10px 24px 6px",
      gap: 12,
      "&::before, &::after": {
        content: '\'\'',
        flex: 1,
        height: 1,
        background: "#EF4444",
        opacity: 0.65,
      },
    },
    unreadSepText: {
      fontSize: 11,
      fontWeight: 700,
      color: "#EF4444",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      whiteSpace: "nowrap",
    },
    typingIndicator: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "4px 28px 2px",
      fontSize: 12,
      color: dark ? "#A1A1AA" : "#71717A",
      fontStyle: "italic",
      minHeight: 22,
      "& > span:last-child": { letterSpacing: 0.2 },
    },
    codeBlock: {
      display: "block",
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 8,
      padding: "10px 12px",
      margin: "6px 0",
      fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 13,
      color: dark ? "#E4E4E7" : "#27272A",
      overflowX: "auto",
      whiteSpace: "pre",
      lineHeight: 1.5,
    },
    codeInline: {
      fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: "0.92em",
      padding: "1px 6px",
      borderRadius: 4,
      background: dark ? "rgba(91,124,255,0.18)" : "rgba(61,92,253,0.12)",
      color: dark ? "#5B7CFF" : "#3D5CFD",
      border: `1px solid ${dark ? "rgba(91,124,255,0.25)" : "rgba(61,92,253,0.2)"}`,
    },
    msgLink: {
      color: dark ? "#5B7CFF" : "#3D5CFD",
      textDecoration: "none",
      borderBottom: `1px dashed ${dark ? "rgba(91,124,255,0.4)" : "rgba(61,92,253,0.4)"}`,
      "&:hover": { textDecoration: "underline" },
    },
    menuPaper: {
      borderRadius: 14,
      background: dark ? "rgba(24,24,27,0.98)" : "#fff",
      minWidth: 260,
      padding: 8,
      boxShadow: dark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 12px 40px rgba(0,0,0,0.15)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
    },
    menuSectionLabel: {
      fontSize: 10,
      fontWeight: 700,
      color: dark ? "#71717A" : "#A1A1AA",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      padding: "8px 12px 4px",
    },
    menuItemStyled: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "8px 12px",
      borderRadius: 10,
      cursor: "pointer",
      transition: "background 0.1s",
      fontSize: 14,
      color: dark ? "#FAFAFA" : "#09090B",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
      },
    },
    menuItemIconBox: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    menuItemText: {
      flex: 1,
      fontWeight: 500,
    },
    menuItemDestructive: {
      color: "#EF4444 !important",
    },
    dialogPaper: {
      borderRadius: 16,
      background: dark ? "#0F0F12" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      overflow: "hidden",
    },
    dialogTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "18px 20px 6px",
    },
    dialogTitleText: {
      fontSize: 18,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: 0.2,
    },
    dialogCloseBtn: {
      padding: 6,
      borderRadius: 8,
      color: dark ? "rgba(248,250,252,0.72)" : "#6b7280",
      transition: "background 0.12s, color 0.12s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        color: dark ? "#f8fafc" : "#111827",
      },
    },
    // Inputs do modal (ex.: Definir status) — borda sutil alinhada ao modal
    // Transferir Ticket, no lugar da borda branca padrão do MUI outlined.
    // !important porque o MUI v4 injeta o estilo do notchedOutline depois.
    dialogTextField: {
      "& .MuiOutlinedInput-root": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
        borderRadius: 12,
      },
      "& .MuiInputBase-input": { color: dark ? "#FAFAFA" : "#09090B" },
      "& .MuiInputBase-input::placeholder": { color: dark ? "#71717A" : "#A1A1AA", opacity: 1 },
      // !important: o MUI v4 injeta o estilo do notchedOutline depois, então sem
      // isso a borda branca padrão (0.23) ganha mesmo com especificidade maior.
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)") + " !important",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: (dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)") + " !important",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: (dark ? "#5B7CFF" : "#3D5CFD") + " !important",
        borderWidth: "1px !important",
      },
    },
    dialogSearchWrap: {
      padding: "8px 20px 12px",
    },
    dialogSearchInput: {
      width: "100%",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 12,
      padding: "10px 12px 10px 38px",
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14,
      outline: "none",
      "&:focus": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
      },
      "&::placeholder": {
        color: dark ? "#71717A" : "#A1A1AA",
      },
    },
    dialogListWrap: {
      padding: "4px 12px 16px",
      maxHeight: 380,
      overflowY: "auto",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      borderRadius: 12,
      margin: "0 20px 12px",
      background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
    },
    dialogListItem: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 12px",
      borderRadius: 10,
      cursor: "pointer",
      transition: "background 0.1s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
      },
    },
    dialogActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: "12px 20px 18px",
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
    },
    primaryBtn: {
      background: dark ? "#5B7CFF" : "#3D5CFD",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      padding: "9px 18px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "filter 0.1s",
      "&:hover": { filter: "brightness(1.1)" },
      "&:disabled": { opacity: 0.5, cursor: "default" },
    },
    ghostBtn: {
      background: "transparent",
      color: dark ? "#A1A1AA" : "#71717A",
      border: "none",
      borderRadius: 10,
      padding: "9px 14px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
      "&:hover": { color: dark ? "#FAFAFA" : "#09090B" },
    },
    typingDots: {
      display: "inline-flex",
      gap: 3,
      "& span": {
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: dark ? "#A1A1AA" : "#71717A",
        animation: "$sttelarTypingBounce 1.2s infinite ease-in-out",
      },
      "& span:nth-child(2)": { animationDelay: "0.2s" },
      "& span:nth-child(3)": { animationDelay: "0.4s" },
    },
    "@keyframes sttelarTypingBounce": {
      "0%, 60%, 100%": { opacity: 0.4, transform: "translateY(0)" },
      "30%": { opacity: 1, transform: "translateY(-3px)" },
    },
    systemMessage: {
      display: "flex",
      justifyContent: "center",
      padding: "6px 24px",
    },
    systemMessageText: {
      fontSize: 12,
      fontStyle: "italic",
      color: dark ? "#71717A" : "#A1A1AA",
      background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
      padding: "4px 12px",
      borderRadius: 12,
    },
    messageRow: {
      display: "flex",
      padding: "3px 24px",
      position: "relative",
      transition: "background 0.1s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
        "& $messageHoverActions": {
          opacity: 1,
          transform: "translateY(0)",
          pointerEvents: "auto",
        },
        "& $groupedTime": {
          opacity: 1,
        },
      },
    },
    messageAvatarCol: {
      width: 40,
      flexShrink: 0,
      paddingTop: 3,
    },
    messageAvatar: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      color: "#fff",
      fontSize: 12,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "transform 0.15s",
      "&:hover": { transform: "scale(1.08)" },
    },
    messageContent: {
      flex: 1,
      minWidth: 0,
      paddingLeft: 12,
    },
    messageHeader: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      marginBottom: 2,
    },
    messageSender: {
      fontSize: 14,
      fontWeight: 700,
      color: text0,
      cursor: "pointer",
      transition: "color 0.15s",
      "&:hover": { color: accent },
    },
    messageBadge: {
      fontSize: 10,
      fontWeight: 600,
      padding: "2px 6px",
      borderRadius: 4,
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      color: dark ? "#5B7CFF" : "#3D5CFD",
    },
    messageTime: {
      fontSize: 11,
      color: text2,
      fontWeight: 500,
    },
    messageBody: {
      fontSize: 14,
      color: dark ? "rgba(250,250,250,0.82)" : "#3F3F46",
      lineHeight: 1.6,
      wordBreak: "break-word",
      "& strong": { fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" },
      "& em": { fontStyle: "italic" },
    },
    groupedTime: {
      fontSize: 10,
      fontWeight: 500,
      color: text2,
      opacity: 0,
      transition: "opacity 0.15s",
      width: 40,
      flexShrink: 0,
      textAlign: "center",
      paddingTop: 4,
    },
    messageDeleted: {
      fontSize: 13,
      fontStyle: "italic",
      color: text2,
    },
    messageEdited: {
      fontSize: 10,
      fontWeight: 500,
      color: text2,
      marginLeft: 6,
    },
    messageVideo: {
      maxWidth: 420,
      maxHeight: 320,
      borderRadius: 10,
      marginTop: 6,
      background: "#000",
      display: "block",
    },
    messageAudio: {
      width: 320,
      maxWidth: "100%",
      marginTop: 6,
      display: "block",
    },
    messageMedia: {
      maxWidth: 380,
      maxHeight: 320,
      borderRadius: 12,
      marginTop: 8,
      cursor: "pointer",
      border: `1px solid ${border}`,
      transition: "all 0.2s",
      "&:hover": {
        borderColor: borderStrong,
        boxShadow: `0 4px 20px ${dark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.08)"}`,
      },
    },
    messageFile: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderRadius: 10,
      marginTop: 8,
      background: dark ? "rgba(255,255,255,0.04)" : bg2,
      border: `1px solid ${border}`,
      color: dark ? "#5B7CFF" : "#3D5CFD",
      fontSize: 13,
      fontWeight: 500,
      textDecoration: "none",
      transition: "all 0.15s",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
        borderColor: `${accent}40`,
      },
    },
    messageHoverActions: {
      position: "absolute",
      top: -14,
      right: 24,
      display: "flex",
      alignItems: "center",
      gap: 2,
      background: glassStrong,
      border: `1px solid ${borderStrong}`,
      borderRadius: 10,
      padding: "3px 5px",
      boxShadow: dark
        ? "0 4px 24px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.2)"
        : "0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)",
      opacity: 0,
      transform: "translateY(4px)",
      pointerEvents: "none",
      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
      zIndex: 10,
    },
    hoverActionBtn: {
      padding: 5,
      borderRadius: 6,
      color: text2,
      transition: "all 0.15s",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
        color: dark ? "#5B7CFF" : "#3D5CFD",
      },
    },
    reactionRow: {
      display: "flex",
      gap: 4,
      flexWrap: "wrap",
      marginTop: 6,
    },
    reactionChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 12,
      cursor: "pointer",
      background: dark ? "rgba(255,255,255,0.04)" : bg2,
      border: `1px solid ${border}`,
      color: dark ? "#A1A1AA" : "#71717A",
      fontWeight: 500,
      transition: "all 0.15s",
      "&:hover": {
        borderColor: borderStrong,
        background: dark ? "rgba(255,255,255,0.06)" : "#f0f0f0",
      },
    },
    reactionChipActive: {
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      borderColor: `${accent}40`,
      color: dark ? "#5B7CFF" : "#3D5CFD",
      fontWeight: 600,
    },

    // ═══ INPUT ═══
    inputContainer: {
      padding: "0 24px 20px",
      flexShrink: 0,
    },
    inputBox: {
      border: `1px solid ${border}`,
      borderRadius: 14,
      background: dark ? "rgba(255,255,255,0.03)" : "#fff",
      overflow: "hidden",
      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
      "&:focus-within": {
        borderColor: `${accent}60`,
        boxShadow: `0 0 0 3px ${accentSoft}, 0 4px 16px ${dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)"}`,
      },
    },
    inputField: {
      width: "100%",
      border: "none",
      outline: "none",
      background: "transparent",
      padding: "14px 18px 8px",
      fontSize: 14,
      color: text0,
      fontFamily: "inherit",
      resize: "none",
      lineHeight: 1.55,
      minHeight: 24,
      maxHeight: 200,
      "&::placeholder": {
        color: text2,
      },
    },
    inputToolbar: {
      display: "flex",
      alignItems: "center",
      padding: "4px 10px 8px",
      gap: 2,
    },
    inputToolbarBtn: {
      padding: 6,
      borderRadius: 8,
      color: text2,
      transition: "all 0.15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        color: text0,
      },
    },
    inputToolbarDivider: {
      width: 1,
      height: 18,
      background: border,
      margin: "0 6px",
    },
    sendBtn: {
      marginLeft: "auto",
      padding: "6px 14px",
      borderRadius: 10,
      color: "#fff",
      background: `linear-gradient(135deg, ${accent}, ${dark ? "#818CF8" : "#5B7CFF"})`,
      transition: "all 0.2s",
      "&:hover": {
        background: `linear-gradient(135deg, ${dark ? "#4A63E8" : "#2A4BDB"}, ${accent})`,
        boxShadow: `0 4px 12px ${accentGlow}`,
      },
      "&.Mui-disabled": {
        background: dark ? "rgba(255,255,255,0.04)" : "#f0f0f0",
        color: text2,
      },
    },
    attachmentPreview: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      fontSize: 12,
      color: dark ? "#A1A1AA" : "#71717A",
    },

    // ═══ MEMBERS PANEL ═══
    membersPanel: {
      width: 260,
      minWidth: 260,
      borderLeft: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      background: dark
        ? "linear-gradient(180deg, rgba(24,24,27,0.95) 0%, rgba(10,10,10,0.95) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
    },
    threadPanel: {
      width: 420,
      minWidth: 420,
      borderLeft: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      background: dark
        ? "linear-gradient(180deg, rgba(24,24,27,0.97) 0%, rgba(10,10,10,0.97) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
    },
    threadBody: {
      flex: 1,
      overflowY: "auto",
      padding: "12px 0",
    },
    threadParentMsg: {
      padding: "10px 18px 14px",
      borderBottom: `1px solid ${border}`,
    },
    threadDivider: {
      display: "flex",
      alignItems: "center",
      padding: "12px 18px 6px",
      gap: 10,
      "&::after": {
        content: '\'\'',
        flex: 1,
        height: 1,
        background: border,
      },
    },
    threadDividerText: {
      fontSize: 11,
      fontWeight: 600,
      color: text2,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      whiteSpace: "nowrap",
    },
    threadReply: {
      padding: "8px 18px",
      transition: "background 0.1s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
      },
    },
    threadInputContainer: {
      borderTop: `1px solid ${border}`,
      padding: "10px 12px",
      display: "flex",
      alignItems: "flex-end",
      gap: 6,
      background: dark ? "rgba(10,10,10,0.6)" : "rgba(255,255,255,0.6)",
    },
    threadReplyCountLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      marginTop: 4,
      padding: "3px 8px",
      borderRadius: 6,
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      color: dark ? "#5B7CFF" : "#3D5CFD",
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.1s",
      "&:hover": {
        background: accentGlow,
      },
    },
    membersPanelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 16px 12px",
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
    },
    membersPanelTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: text0,
      letterSpacing: "-0.2px",
    },
    membersList: {
      flex: 1,
      overflowY: "auto",
      padding: "8px 8px",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        borderRadius: 2,
      },
    },
    memberItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 8px",
      borderRadius: 8,
      transition: "background 0.1s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      },
    },
    memberAvatar: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    memberInfo: {
      flex: 1,
      minWidth: 0,
    },
    memberName: {
      fontSize: 13,
      fontWeight: 600,
      color: text0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    memberRole: {
      fontSize: 11,
      color: text2,
      fontWeight: 500,
    },
    addMemberSection: {
      padding: "8px 8px",
      borderTop: `1px solid ${border}`,
    },
    addMemberBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      width: "100%",
      padding: "8px 12px",
      border: `1px dashed ${borderStrong}`,
      borderRadius: 10,
      background: "transparent",
      color: text2,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 600,
      transition: "all 0.15s",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
        borderColor: `${accent}40`,
        color: dark ? "#5B7CFF" : "#3D5CFD",
      },
    },
    emptyState: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: 40,
    },

    // ═══ MENTIONS ═══
    mentionDropdown: {
      position: "absolute",
      bottom: "100%",
      left: 0,
      right: 0,
      marginBottom: 4,
      background: glassStrong,
      border: `1px solid ${borderStrong}`,
      borderRadius: 12,
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)"
        : "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
      maxHeight: 240,
      overflowY: "auto",
      zIndex: 100,
      padding: "6px 0",
      animation: "$sttelarSlideIn 0.15s ease-out",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        borderRadius: 2,
      },
    },
    mentionItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 14px",
      cursor: "pointer",
      transition: "background 0.1s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      },
    },
    mentionItemActive: {
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      },
    },
    mentionItemAvatar: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    mentionItemName: {
      fontSize: 13,
      fontWeight: 600,
      color: text0,
    },
    mentionItemSub: {
      fontSize: 11,
      color: text2,
      fontWeight: 500,
    },
    mentionHighlight: {
      color: dark ? "#5B7CFF" : "#3D5CFD",
      fontWeight: 600,
      backgroundColor: accentSoft,
      padding: "1px 3px",
      borderRadius: 4,
      cursor: "pointer",
      transition: "background 0.15s",
      "&:hover": {
        backgroundColor: accentGlow,
      },
    },
    mentionToast: {
      "& .MuiSnackbarContent-root": {
        background: dark
          ? "linear-gradient(135deg, #1E1E22, #2A2A30)"
          : "linear-gradient(135deg, #fff, #fafafa)",
        color: text0,
        border: `1px solid ${borderStrong}`,
        borderRadius: 12,
        boxShadow: dark
          ? "0 8px 32px rgba(0,0,0,0.4)"
          : "0 8px 32px rgba(0,0,0,0.1)",
        minWidth: 280,
        padding: "8px 16px",
      },
    },
    mentionHint: {
      fontSize: 11,
      color: text2,
      padding: "4px 14px 6px",
      borderTop: `1px solid ${border}`,
      marginTop: 2,
    },
  };
});

const QUICK_EMOJIS = ["\u{1F44D}", "\u{1F60A}", "\u2764\uFE0F", "\u{1F602}", "\u{1F389}", "\u{1F440}", "\u{1F525}", "\u2705"];

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const formatDateSep = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hoje";
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
};

const AvatarEl = ({ name, profileImage, size = 36, fontSize = 12, className, style }) => {
  const [g1, g2] = getGradient(name);
  if (profileImage) {
    return (
      <img
        src={profileImage}
        alt={name}
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          ...style,
        }}
      />
    );
  }
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${g1}, ${g2})`,
        color: "#fff",
        fontSize,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...style,
      }}
    >
      {getInitials(name)}
    </div>
  );
};

const parseMessageBody = (text, mentionClass, classes, mentionUsers) => {
  if (!text) return text;
  // Pre-process: substituir cada @<full-name> por um token único pra preservar nomes com espaço
  // Estratégia: pegar os nomes em ordem decrescente de tamanho (longest first) pra evitar matches parciais
  let processedText = text;
  const mentionTokens = new Map(); // tokenIdx -> {name}
  if (mentionUsers && mentionUsers.length > 0) {
    const sorted = mentionUsers
      .filter(u => u && u.name)
      .slice()
      .sort((a, b) => b.name.length - a.name.length);
    sorted.forEach((u, idx) => {
      // Escapar regex special chars do nome
      const escaped = u.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp("@" + escaped + "(?![\\w])", "g");
      const token = `\u0000MENT${idx}\u0000`;
      if (re.test(processedText)) {
        mentionTokens.set(idx, u);
        processedText = processedText.replace(re, token);
      }
    });
  }
  text = processedText;

  // ── 1) Extrair code blocks ```code``` em blocos completos primeiro ──
  const blocks = [];
  const codeRegex = /```(\w+)?\n?([\s\S]*?)```/g;
  let lastIdx = 0;
  let cm;
  while ((cm = codeRegex.exec(text)) !== null) {
    if (cm.index > lastIdx) blocks.push({ type: "text", value: text.substring(lastIdx, cm.index) });
    blocks.push({ type: "code", lang: cm[1] || "", value: cm[2] || "" });
    lastIdx = cm.index + cm[0].length;
  }
  if (lastIdx < text.length) blocks.push({ type: "text", value: text.substring(lastIdx) });
  if (blocks.length === 0) blocks.push({ type: "text", value: text });

  const blockKey = { v: 0 };
  return blocks.flatMap(block => {
    if (block.type === "code") {
      return [
        <pre key={`b${blockKey.v++}`} className={(classes && classes.codeBlock) || undefined}>
          <code>{block.value}</code>
        </pre>
      ];
    }
    // texto normal — quebrar em linhas e processar inline
    const lines = block.value.split("\n");
    return lines.map((line, lineIdx) => {
      const parts = [];
      let remaining = line;
      let key = 0;

      while (remaining.length > 0) {
        // Token de mention preservada (nomes com espaço)
        const tokenMatch = remaining.match(/\u0000MENT(\d+)\u0000/);
        if (tokenMatch && tokenMatch.index === 0) {
          const idx = parseInt(tokenMatch[1], 10);
          const u = mentionTokens.get(idx);
          if (u) {
            parts.push(<span key={`k${blockKey.v}-${key++}`} className={mentionClass}>@{u.name}</span>);
            remaining = remaining.substring(tokenMatch[0].length);
            continue;
          }
        }
        // Token em meio ao texto: pega prefix + token
        if (tokenMatch && tokenMatch.index > 0) {
          parts.push(remaining.substring(0, tokenMatch.index));
          const idx = parseInt(tokenMatch[1], 10);
          const u = mentionTokens.get(idx);
          if (u) {
            parts.push(<span key={`k${blockKey.v}-${key++}`} className={mentionClass}>@{u.name}</span>);
            remaining = remaining.substring(tokenMatch.index + tokenMatch[0].length);
            continue;
          }
        }
        const codeInlineMatch = remaining.match(/`([^`]+)`/);
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const italicMatch = remaining.match(/_(.+?)_/);
        const mentionMatch = remaining.match(/@([\w\sÀ-ÿ]+?)(?=\s|$|[.,!?;:])/);
        const linkMatch = remaining.match(/(https?:\/\/[^\s<>"\]\[]+)/i);
        const matches = [
          codeInlineMatch ? { idx: codeInlineMatch.index, match: codeInlineMatch, type: "code" } : null,
          boldMatch ? { idx: boldMatch.index, match: boldMatch, type: "bold" } : null,
          italicMatch ? { idx: italicMatch.index, match: italicMatch, type: "italic" } : null,
          mentionMatch ? { idx: mentionMatch.index, match: mentionMatch, type: "mention" } : null,
          linkMatch ? { idx: linkMatch.index, match: linkMatch, type: "link" } : null,
        ].filter(Boolean).sort((a, b) => a.idx - b.idx);

        if (matches.length === 0) { parts.push(remaining); break; }
        const first = matches[0];
        if (first.idx > 0) parts.push(remaining.substring(0, first.idx));
        if (first.type === "bold") {
          parts.push(<strong key={`k${blockKey.v}-${key++}`}>{first.match[1]}</strong>);
        } else if (first.type === "italic") {
          parts.push(<em key={`k${blockKey.v}-${key++}`}>{first.match[1]}</em>);
        } else if (first.type === "code") {
          parts.push(<code key={`k${blockKey.v}-${key++}`} className={(classes && classes.codeInline) || undefined}>{first.match[1]}</code>);
        } else if (first.type === "link") {
          const url = first.match[1];
          parts.push(<a key={`k${blockKey.v}-${key++}`} href={url} target="_blank" rel="noopener noreferrer" className={(classes && classes.msgLink) || undefined}>{url}</a>);
        } else if (first.type === "mention") {
          parts.push(
            <span key={`k${blockKey.v}-${key++}`} className={mentionClass}>@{first.match[1]}</span>
          );
        }
        remaining = remaining.substring(first.idx + first.match[0].length);
    }

    const isBullet = line.startsWith("\u2022 ") || line.startsWith("- ");
    const content = isBullet ? parts.slice(0).map((p, i) =>
      typeof p === "string" && i === 0 ? p.replace(/^[\u2022\-]\s/, "") : p
    ) : parts;

    return (
      <React.Fragment key={lineIdx}>
        {lineIdx > 0 && <br />}
        {isBullet ? <span style={{ paddingLeft: 8 }}>{"\u2022 "}{content}</span> : content}
      </React.Fragment>
    );
    });
  });
};

/* ═══════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════ */

const CustomModal = ({ open, onClose, width, children, dark }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1300,
        background: "rgba(0,0,0,0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: width || 480,
          maxWidth: "calc(100% - 32px)",
          maxHeight: "calc(100% - 64px)",
          background: dark ? "#0F0F12" : "#fff",
          borderRadius: 16,
          border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          boxShadow: dark ? "0 20px 60px rgba(0,0,0,0.6)" : "0 20px 60px rgba(0,0,0,0.2)",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const InternalChat = () => {
  const classes = useStyles();
  const theme = useTheme();
  const dark = theme.palette.type === "dark";
  const accent = dark ? "#5B7CFF" : "#3D5CFD";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { channelId: urlChannelId } = useParams();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesNextCursor, setMessagesNextCursor] = useState(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Cargo (JobRole) — usuário básico precisa escolher antes de usar o Chat
  // Interno. Admin/super não passam pelo modal. Estado começa "true" se já
  // tem cargo OU se é admin/super, "false" caso contrário (abre modal).
  const initialHasJobRole = !!user?.jobRoleId
    || !!user?.jobRole
    || user?.profile === "admin"
    || user?.super === true;
  const [hasJobRole, setHasJobRole] = useState(initialHasJobRole);
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileSidebar, setShowMobileSidebar] = useState(!urlChannelId);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [channelsOpen, setChannelsOpen] = useState(true);
  const [dmsOpen, setDmsOpen] = useState(true);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [channelBrowserOpen, setChannelBrowserOpen] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [loadingBrowser, setLoadingBrowser] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDesc, setNewChannelDesc] = useState("");
  const [newDMOpen, setNewDMOpen] = useState(false);
  const [dmSelected, setDmSelected] = useState([]); // array of user objects
  const [dmSearch, setDmSearch] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState("");
  const [unreadDividerMsgId, setUnreadDividerMsgId] = useState(null);
  const [threadParentMsg, setThreadParentMsg] = useState(null);
  const [threadText, setThreadText] = useState("");
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [msgSearchOpen, setMsgSearchOpen] = useState(false);
  const [channelMenuAnchor, setChannelMenuAnchor] = useState(null);
  const [channelMenuTarget, setChannelMenuTarget] = useState(null);
  const [typingUsers, setTypingUsers] = useState({}); // {channelId: {userId: {name, ts}}}
  const [activePanel, setActivePanel] = useState(null); // "pinned" | "saved" | "mentions"
  const [panelMessages, setPanelMessages] = useState([]);
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [savedMessageIds, setSavedMessageIds] = useState(new Set());
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusEmojiInput, setStatusEmojiInput] = useState(user?.statusEmoji || "");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  const [profileCard, setProfileCard] = useState(null); // {user, x, y}
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiAnchorRect, setEmojiAnchorRect] = useState(null);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  const [reactionAnchorRect, setReactionAnchorRect] = useState(null);
  const [myStatus, setMyStatus] = useState({ emoji: user?.statusEmoji || "", text: user?.statusText || "" });
  const [statusTextInput, setStatusTextInput] = useState(user?.statusText || "");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [loadingGlobalSearch, setLoadingGlobalSearch] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const lastTypingEmitRef = useRef(0);
  const typingTimeoutsRef = useRef({});
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [mentionStartPos, setMentionStartPos] = useState(null);
  const [mentionToast, setMentionToast] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionedChannels, setMentionedChannels] = useState(new Set());

  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  // Sinaliza "pular pro fim" na carga inicial de um canal (consumido pelo
  // useLayoutEffect abaixo). Setada no load effect ANTES de setMessages.
  const pendingInitialScrollRef = useRef(false);
  // Cache de mensagens por canal (channelId -> lista): render instantâneo ao
  // (re)abrir uma conversa já visitada + revalidação em background.
  // messagesChannelRef guarda a qual canal o estado `messages` pertence, p/
  // espelhar no cache certo (não no selectedChannelId, que muda antes no switch).
  const messagesCacheRef = useRef({});
  const messagesChannelRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const getInternalChatUserName = useCallback((targetUser) => {
    return targetUser?.name || "Desconhecido";
  }, []);

  const getInternalChatDisplayName = useCallback((targetUser) => {
    const rawName = getInternalChatUserName(targetUser);
    const jobRoleName = targetUser?.jobRole?.name?.trim();

    return jobRoleName ? `${rawName} \u00B7 ${jobRoleName}` : rawName;
  }, [getInternalChatUserName]);

  // ── Data ──
  const fetchChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-chat/channels");
      setChannels(data);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-chat/users");
      setCompanyUsers(data.filter(u => u.id !== user.id));
    } catch (err) { /* ignore */ }
  }, [user.id]);

  useEffect(() => { fetchChannels(); fetchUsers(); }, [fetchChannels, fetchUsers]);

  // Fetch initial status do próprio user
  useEffect(() => {
    api.get("/internal-chat/users").then(({ data }) => {
      const me = (data || []).find(u => u.id === user.id);
      if (me) setMyStatus({ emoji: me.statusEmoji || "", text: me.statusText || "" });
    }).catch(() => {});
  }, [user.id]);

  // Fetch initial saved message IDs
  useEffect(() => {
    api.get("/internal-chat/saved").then(({ data }) => {
      setSavedMessageIds(new Set((data || []).map(m => m.id)));
    }).catch(() => {});
  }, []);

  // Esc fecha emoji picker
  useEffect(() => {
    if (!showEmoji) return;
    const onKey = (e) => { if (e.key === "Escape") setShowEmoji(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showEmoji]);

  // Atalho Ctrl/Cmd+F para abrir busca de mensagens local
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f" && selectedChannelId) {
        e.preventDefault();
        setMsgSearchOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedChannelId]);

  useEffect(() => {
    if (urlChannelId && channels.length > 0) {
      const id = parseInt(urlChannelId, 10);
      if (id !== selectedChannelId) { setSelectedChannelId(id); setShowMobileSidebar(false); }
    }
  }, [urlChannelId, channels]);

  useEffect(() => {
    if (!selectedChannelId) return;
    let cancelled = false;

    // Cache por canal: se já visitamos este canal, renderiza instantâneo do
    // cache e pula pro fim; depois revalida em background. Primeira visita mostra
    // o loader e busca normalmente.
    const cached = messagesCacheRef.current[selectedChannelId];
    if (cached) {
      messagesChannelRef.current = selectedChannelId;
      pendingInitialScrollRef.current = true;
      setMessages(cached);
      setMessagesHasMore(false);     // a revalidação corrige a paginação
      setMessagesNextCursor(null);
      setLoadingMessages(false);
    } else {
      setLoadingMessages(true);
      setMessagesHasMore(false);
      setMessagesNextCursor(null);
    }

    const loadMessages = async () => {
      try {
        const { data } = await api.get(`/internal-chat/channels/${selectedChannelId}/messages`, { params: { limit: 50 } });
        if (cancelled) return;
        // Backend retorna { messages, hasMore, nextCursor }. Compat com array puro.
        const list = Array.isArray(data) ? data : (data.messages || []);
        messagesCacheRef.current[selectedChannelId] = list;
        messagesChannelRef.current = selectedChannelId;
        // Só na PRIMEIRA exibição (sem cache) este é o "abrir" → pula pro fim
        // (com cache o jump já aconteceu no render instantâneo acima). Setar
        // ANTES de setMessages: React 17 não agrupa setState em callback async.
        if (!cached) pendingInitialScrollRef.current = true;
        setMessages(list);
        setMessagesHasMore(Array.isArray(data) ? false : !!data.hasMore);
        setMessagesNextCursor(Array.isArray(data) ? null : (data.nextCursor || null));
        setLoadingMessages(false);
        // Calcula divider de "Novas Mensagens" antes de marcar como lido
        const currentChannel = channels.find(ch => ch.id === selectedChannelId);
        const lastReadId = currentChannel?.lastReadMessageId || 0;
        const firstUnread = list.find(m => m.id > lastReadId && m.senderId !== user.id);
        setUnreadDividerMsgId(firstUnread ? firstUnread.id : null);
        api.post(`/internal-chat/channels/${selectedChannelId}/read`).catch(() => {});
        setChannels(prev => prev.map(ch => ch.id === selectedChannelId ? { ...ch, unreadCount: 0, lastReadMessageId: list[list.length - 1]?.id || ch.lastReadMessageId } : ch));
      } catch (err) { if (!cancelled) { toastError(err); setLoadingMessages(false); } }
    };
    loadMessages();
    return () => { cancelled = true; };
  }, [selectedChannelId]);

  // Mantém o cache do canal EXIBIDO sempre fresco (mensagem nova via socket,
  // envio, edição, exclusão, load-older). Usa messagesChannelRef (a qual canal
  // `messages` pertence) — no switch, selectedChannelId muda antes de `messages`,
  // então gravar por selectedChannelId corromperia o cache do canal novo.
  useEffect(() => {
    // Só espelha quando `messages` de fato pertence ao canal selecionado — evita
    // corromper o cache do canal anterior caso uma mensagem do canal novo chegue
    // via socket na janela de carregamento (antes do fetch retornar).
    if (messagesChannelRef.current != null && messagesChannelRef.current === selectedChannelId) {
      messagesCacheRef.current[messagesChannelRef.current] = messages;
    }
  }, [messages, selectedChannelId]);

  // Ao abrir/trocar de conversa, já deixa o cursor no campo de mensagem (desktop)
  // — evita ter que clicar na caixa pra digitar. No mobile não focamos, p/ não
  // abrir o teclado por cima da conversa automaticamente.
  useEffect(() => {
    if (!selectedChannelId || isMobile) return;
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [selectedChannelId, isMobile]);

  // Carrega página mais antiga (scroll reverso). Preserva posição visual
  // ancorando no primeiro item visível antes do prepend.
  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderMessages || !messagesHasMore || !messagesNextCursor || !selectedChannelId) return;
    setLoadingOlderMessages(true);
    const listEl = messageListRef.current;
    const prevScrollHeight = listEl ? listEl.scrollHeight : 0;
    const prevScrollTop = listEl ? listEl.scrollTop : 0;
    try {
      const { data } = await api.get(`/internal-chat/channels/${selectedChannelId}/messages`, {
        params: { limit: 50, cursor: messagesNextCursor }
      });
      const older = Array.isArray(data) ? data : (data.messages || []);
      setMessages(prev => [...older, ...prev]);
      setMessagesHasMore(Array.isArray(data) ? false : !!data.hasMore);
      setMessagesNextCursor(Array.isArray(data) ? null : (data.nextCursor || null));
      // Ajusta scroll pra manter posição do usuário (não pula pro topo).
      requestAnimationFrame(() => {
        if (messageListRef.current) {
          const delta = messageListRef.current.scrollHeight - prevScrollHeight;
          messageListRef.current.scrollTop = prevScrollTop + delta;
        }
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [loadingOlderMessages, messagesHasMore, messagesNextCursor, selectedChannelId]);

  // Filtro da busca de mensagens — memoizado pra não refiltrar a cada
  // keystroke quando o array está grande.
  const visibleMessages = useMemo(() => {
    if (!msgSearchQuery.trim()) return messages;
    const q = msgSearchQuery.toLowerCase();
    return messages.filter(m =>
      (m.body || "").toLowerCase().includes(q) ||
      (m.sender?.name || "").toLowerCase().includes(q)
    );
  }, [messages, msgSearchQuery]);

  // Scroll para o fim só quando troca de canal (initial load) ou quando o
  // usuário envia/recebe msg estando no fim. Não executa em loadOlder pra
  // preservar a posição do usuário durante o scroll reverso.
  // Carga inicial de um canal → pula pro fim. Executa em useLayoutEffect (antes
  // do paint, sem "ver o topo e pular"), rolando o container direto
  // (scrollTop = scrollHeight, mais confiável que scrollIntoView num sentinela).
  // O rAF de reforço sobrevive a avatar/imagem/anexo que cresce após o 1º layout.
  useLayoutEffect(() => {
    if (!pendingInitialScrollRef.current) return;
    const el = messageListRef.current;
    if (!el) return;
    pendingInitialScrollRef.current = false;
    el.scrollTop = el.scrollHeight;
    requestAnimationFrame(() => {
      const el2 = messageListRef.current;
      if (el2) el2.scrollTop = el2.scrollHeight;
    });
  }, [messages]);

  // Mensagem nova ao vivo: se o usuário já está perto do fim, acompanha; se está
  // lendo o histórico mais acima, não força a rolagem.
  useEffect(() => {
    if (!messages.length) return;
    const listEl = messageListRef.current;
    if (!listEl) return;
    const distanceFromBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
    if (distanceFromBottom < 120 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ── Socket ──
  const groupName = user?.company?.groupName;
  const chatRoom = groupName ? `group-${groupName}-internal-chat` : `company-${user.companyId}-internal-chat`;

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    socket.emit("joinInternalChat", groupName ? { groupName } : undefined);
    const handleChatEvent = (data) => {
      if (data.action === "newMessage") {
        setChannels(prev => {
          const updated = prev.map(ch => ch.id === data.channelId ? {
            ...ch, lastMessage: data.message.body, lastMessageAt: data.message.createdAt,
            unreadCount: ch.id === selectedChannelId ? 0 : (ch.unreadCount || 0) + 1
          } : ch);
          return updated.sort((a, b) => {
            if (!a.lastMessageAt) return 1; if (!b.lastMessageAt) return -1;
            return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
          });
        });
      }
      if (data.action === "newChannel") fetchChannels();
      if (data.action === "updateChannel" || data.action === "archiveChannel" || data.action === "deleteChannel") {
        fetchChannels();
        if (data.action === "deleteChannel" && data.channelId === selectedChannelId) {
          setSelectedChannelId(null); setMessages([]);
        }
      }
    };
    socket.on(chatRoom, handleChatEvent);
    return () => { socket.emit("leaveInternalChat", groupName ? { groupName } : undefined); socket.off(chatRoom, handleChatEvent); };
  }, [socketManager, user.companyId, groupName, chatRoom, selectedChannelId, fetchChannels]);

  useEffect(() => {
    if (!selectedChannelId) return;
    const socket = socketManager.getSocket(user.companyId);
    const doJoin = () => socket.emit("joinInternalChannel", String(selectedChannelId));
    doJoin();
    // Rejoin on reconnect (caso socket caia)
    const handleReconnect = () => doJoin();
    socket.on("connect", handleReconnect);
    socket.on("ready", handleReconnect);
    const handleChannelEvent = (data) => {
      if (data.action === "create") {
        setMessages(prev => [...prev, data.message]);
        api.post(`/internal-chat/channels/${selectedChannelId}/read`).catch(() => {});
      }
      if (data.action === "typing") {
        const { channelId, userId, userName } = data;
        if (userId === user.id) return;
        setTypingUsers(prev => {
          const ch = { ...(prev[channelId] || {}) };
          ch[userId] = { name: userName, ts: Date.now() };
          return { ...prev, [channelId]: ch };
        });
        // Auto-expire after 4s
        const key = `${channelId}-${userId}`;
        clearTimeout(typingTimeoutsRef.current[key]);
        typingTimeoutsRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => {
            const ch = { ...(prev[channelId] || {}) };
            delete ch[userId];
            return { ...prev, [channelId]: ch };
          });
        }, 4000);
        return;
      }
      if (data.action === "stopTyping") {
        const { channelId, userId } = data;
        setTypingUsers(prev => {
          const ch = { ...(prev[channelId] || {}) };
          delete ch[userId];
          return { ...prev, [channelId]: ch };
        });
        return;
      }
      if (data.action === "typing") {
        const { channelId, userId, userName } = data;
        if (userId === user.id) return;
        setTypingUsers(prev => {
          const ch = { ...(prev[channelId] || {}) };
          ch[userId] = { name: userName, ts: Date.now() };
          return { ...prev, [channelId]: ch };
        });
        // Auto-expire after 4s
        const key = `${channelId}-${userId}`;
        clearTimeout(typingTimeoutsRef.current[key]);
        typingTimeoutsRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => {
            const ch = { ...(prev[channelId] || {}) };
            delete ch[userId];
            return { ...prev, [channelId]: ch };
          });
        }, 4000);
        return;
      }
      if (data.action === "stopTyping") {
        const { channelId, userId } = data;
        setTypingUsers(prev => {
          const ch = { ...(prev[channelId] || {}) };
          delete ch[userId];
          return { ...prev, [channelId]: ch };
        });
        return;
      }
      if (data.action === "update") setMessages(prev => prev.map(m => {
        if (m.id !== data.message.id) return m;
        // se socket payload não trouxe company, preserva a do estado local
        const incoming = data.message;
        return { ...m, ...incoming, sender: (incoming?.sender?.company ? incoming.sender : m.sender) };
      }));
      if (data.action === "delete") setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, isDeleted: true, body: "" } : m));
      if (data.action === "reaction") setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
    };
    socket.on(`internal-channel-${selectedChannelId}`, handleChannelEvent);
    return () => {
      socket.emit("leaveInternalChannel", String(selectedChannelId));
      socket.off(`internal-channel-${selectedChannelId}`, handleChannelEvent);
      socket.off("connect", handleReconnect);
      socket.off("ready", handleReconnect);
    };
  }, [socketManager, user.companyId, selectedChannelId]);

  // ── Format ──
  const insertFormat = (prefix, suffix) => {
    const ta = textareaRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = messageText.substring(s, e);
    setMessageText(messageText.substring(0, s) + prefix + sel + suffix + messageText.substring(e));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + prefix.length, e + prefix.length); }, 0);
  };
  const handleBold = () => insertFormat("**", "**");
  const handleItalic = () => insertFormat("_", "_");
  const handleList = () => {
    const ta = textareaRef.current; if (!ta) return;
    const s = ta.selectionStart;
    const before = messageText.substring(0, s), after = messageText.substring(s);
    const nl = before.length > 0 && !before.endsWith("\n") ? "\n" : "";
    setMessageText(before + nl + "\u2022 " + after);
    setTimeout(() => { ta.focus(); const p = s + nl.length + 2; ta.setSelectionRange(p, p); }, 0);
  };
  const handleFileSelect = (e) => { const f = e.target.files?.[0]; if (f) setAttachedFile(f); e.target.value = ""; };

  // ── Mentions ──
  // Para menções, usar membros do canal (se carregados), senão fallback para companyUsers
  const mentionCandidates = React.useMemo(() => {
    if (channelMembers.length > 0) {
      return channelMembers
        .map(m => m.user)
        .filter(u => u && u.id !== user.id);
    }
    return companyUsers.filter(u => u.id !== user.id);
  }, [channelMembers, companyUsers, user.id]);

  const filteredMentionUsers = mentionQuery
    ? mentionCandidates.filter(u => u.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 8)
    : mentionCandidates.slice(0, 8);

  // Lista usada pra renderizar mentions com nomes com espaço corretamente
  const allMentionableUsers = React.useMemo(() => {
    const users = [];
    const ids = new Set();
    [...(channelMembers || []), ...(companyUsers || [])].forEach(m => {
      const u = m.user || m;
      if (u && u.id && u.name && !ids.has(u.id)) {
        ids.add(u.id);
        users.push({ id: u.id, name: u.name });
      }
    });
    // Incluir próprio user
    if (user?.id && user?.name && !ids.has(user.id)) {
      users.push({ id: user.id, name: user.name });
    }
    return users;
  }, [channelMembers, companyUsers, user]);

  // Emite "typing" throttled a cada 3s enquanto user digita
  const emitTyping = useCallback(() => {
    if (!selectedChannelId) return;
    const now = Date.now();
    if (now - lastTypingEmitRef.current < 3000) return;
    lastTypingEmitRef.current = now;
    const socket = socketManager.getSocket(user.companyId);
    socket.emit("internalTyping", { channelId: String(selectedChannelId) });
  }, [selectedChannelId, socketManager, user.companyId]);

  const detectMention = (text, cursorPos) => {
    const before = text.substring(0, cursorPos);
    const atIdx = before.lastIndexOf("@");
    if (atIdx === -1) { setShowMentions(false); return; }
    // @ must be at start or after whitespace
    if (atIdx > 0 && !/\s/.test(before[atIdx - 1])) { setShowMentions(false); return; }
    const query = before.substring(atIdx + 1);
    // If there's a space after completed name, close
    if (query.includes("\n")) { setShowMentions(false); return; }
    setMentionQuery(query);
    setMentionStartPos(atIdx);
    setMentionIndex(0);
    setShowMentions(true);
  };

  const insertMention = (targetUser) => {
    const ta = textareaRef.current;
    if (mentionStartPos === null || !ta) return;
    const before = messageText.substring(0, mentionStartPos);
    const cursorPos = ta.selectionStart;
    const after = messageText.substring(cursorPos);
    const newText = before + "@" + targetUser.name + " " + after;
    setMessageText(newText);
    setShowMentions(false);
    setMentionQuery("");
    setMentionStartPos(null);
    // Track mentioned user
    if (!mentionedUsers.find(u => u.id === targetUser.id)) {
      setMentionedUsers(prev => [...prev, { id: targetUser.id, name: targetUser.name }]);
    }
    setTimeout(() => {
      ta.focus();
      const pos = mentionStartPos + targetUser.name.length + 2;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  // ── Reply ──
  const handleReply = (msg) => {
    setReplyingTo(msg);
    setTimeout(() => textareaRef.current?.focus(), 50);
    return;
    // legacy:
    setThreadParentMsg(msg);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // ── Mention notification via socket ──
  const playMentionSound = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o1 = ctx.createOscillator();
      const o2 = ctx.createOscillator();
      const g = ctx.createGain();
      o1.connect(g); o2.connect(g); g.connect(ctx.destination);
      o1.frequency.value = 880; o1.type = "sine";
      o2.frequency.value = 660; o2.type = "sine"; o2.detune.value = 12;
      g.gain.setValueAtTime(0, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.32);
      o1.start(); o2.start();
      o1.stop(ctx.currentTime + 0.32); o2.stop(ctx.currentTime + 0.32);
    } catch (e) { /* ignore */ }
  }, []);

  // Forçar próprio user sempre online (estou conectado, é óbvio)
  useEffect(() => {
    setMessages(prev => prev.map(m => m.sender?.id === user.id ? { ...m, sender: { ...m.sender, online: true } } : m));
    setChannels(prev => prev.map(ch => {
      if (ch.type === "dm" && ch.dmUser?.id === user.id) {
        return { ...ch, dmUser: { ...ch.dmUser, online: true } };
      }
      if (ch.type === "group_dm" && ch.groupMembers) {
        return { ...ch, groupMembers: ch.groupMembers.map(m => m.id === user.id ? { ...m, online: true } : m) };
      }
      return ch;
    }));
    setChannelMembers(prev => prev.map(m => m.user?.id === user.id ? { ...m, user: { ...m.user, online: true } } : m));
  }, [user.id, messages.length, channels.length]);

  // Pedir permissão de notificação no mount
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Listener de presença online/offline (broadcast global)
  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    const handlePresence = (data) => {
      if (!data || typeof data.userId === "undefined") return;
      // Atualiza dmUser.online no canal DM correspondente
      setChannels(prev => prev.map(ch => {
        if (ch.type === "dm" && ch.dmUser?.id === data.userId) {
          return { ...ch, dmUser: { ...ch.dmUser, online: data.online } };
        }
        return ch;
      }));
      // Atualiza membros do canal aberto
      setChannelMembers(prev => prev.map(m => {
        if (m.user?.id === data.userId) {
          return { ...m, user: { ...m.user, online: data.online } };
        }
        return m;
      }));
      // Atualiza presence em mensagens (sender.online)
      setMessages(prev => prev.map(m => {
        if (m.sender?.id === data.userId) {
          return { ...m, sender: { ...m.sender, online: data.online } };
        }
        return m;
      }));
      // Atualiza lista de companyUsers
      setCompanyUsers(prev => prev.map(u => u.id === data.userId ? { ...u, online: data.online } : u));
    };
    socket.on("user-presence", handlePresence);

    const handleStatusUpdate = (data) => {
      if (!data) return;
      if (data.userId === user.id) {
        setMyStatus({ emoji: data.statusEmoji || "", text: data.statusText || "" });
      }
      setMessages(prev => prev.map(m => {
        if (m.sender?.id === data.userId) {
          return { ...m, sender: { ...m.sender, statusEmoji: data.statusEmoji, statusText: data.statusText } };
        }
        return m;
      }));
      setChannels(prev => prev.map(ch => {
        if (ch.type === "dm" && ch.dmUser?.id === data.userId) {
          return { ...ch, dmUser: { ...ch.dmUser, statusEmoji: data.statusEmoji, statusText: data.statusText } };
        }
        return ch;
      }));
      setChannelMembers(prev => prev.map(m => {
        if (m.user?.id === data.userId) {
          return { ...m, user: { ...m.user, statusEmoji: data.statusEmoji, statusText: data.statusText } };
        }
        return m;
      }));
      setCompanyUsers(prev => prev.map(u => u.id === data.userId ? { ...u, statusEmoji: data.statusEmoji, statusText: data.statusText } : u));
    };
    socket.on("user-status-update", handleStatusUpdate);

    // Atualizações de User (inclui mudança de cargo via JobRoleController):
    // backend emite `company-{companyId}-user` com {action:"update", user}
    // após admin renomear/deletar um cargo (loop em emitUpdatedUsers).
    const handleUserUpdate = (data) => {
      if (!data || data.action !== "update" || !data.user) return;
      const updated = data.user;

      // Se foi o próprio user logado: refletir cargo no estado do gate.
      if (updated.id === user.id) {
        const stillHasRole = !!updated.jobRoleId || !!updated.jobRole;
        const isAdminOrSuper = user.profile === "admin" || user.super;
        setHasJobRole(stillHasRole || isAdminOrSuper);
      }

      // Propaga o novo cargo em todas as listagens onde o user aparece.
      const nextJobRole = updated.jobRole || null;
      setChannels(prev => prev.map(ch => {
        if (ch.type === "dm" && ch.dmUser?.id === updated.id) {
          return { ...ch, dmUser: { ...ch.dmUser, jobRole: nextJobRole, jobRoleId: updated.jobRoleId ?? null } };
        }
        if (ch.type === "group_dm" && Array.isArray(ch.groupMembers)) {
          return {
            ...ch,
            groupMembers: ch.groupMembers.map(m =>
              m.id === updated.id ? { ...m, jobRole: nextJobRole, jobRoleId: updated.jobRoleId ?? null } : m
            )
          };
        }
        return ch;
      }));
      setMessages(prev => prev.map(m =>
        m.sender?.id === updated.id
          ? { ...m, sender: { ...m.sender, jobRole: nextJobRole, jobRoleId: updated.jobRoleId ?? null } }
          : m
      ));
      setChannelMembers(prev => prev.map(member =>
        member.user?.id === updated.id
          ? { ...member, user: { ...member.user, jobRole: nextJobRole, jobRoleId: updated.jobRoleId ?? null } }
          : member
      ));
      setCompanyUsers(prev => prev.map(u =>
        u.id === updated.id ? { ...u, jobRole: nextJobRole, jobRoleId: updated.jobRoleId ?? null } : u
      ));
    };
    socket.on(`company-${user.companyId}-user`, handleUserUpdate);

    return () => {
      socket.off("user-presence", handlePresence);
      socket.off("user-status-update", handleStatusUpdate);
      socket.off(`company-${user.companyId}-user`, handleUserUpdate);
    };
  }, [socketManager, user.companyId, user.id, user.profile, user.super]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    const handleMentionNotification = (data) => {
      // ============ Novo canal/DM/GroupDM em tempo real ============
      if (data.action === "channelRenamed") {
        setChannels(prev => prev.map(c => c.id === data.channelId ? { ...c, customName: data.customName } : c));
        return;
      }
      if (data.action === "newInternalChannel") {
        const newCh = data.channel;
        if (!newCh) return;
        setChannels(prev => {
          if (prev.some(c => c.id === newCh.id)) return prev;
          // Adicionar dmUser / groupMembers já preenchidos (vem do backend)
          const others = (newCh.members || []).filter(m => m.userId !== user.id && m.user);
          const enriched = { ...newCh, unreadCount: 0, lastReadMessageId: 0, isFavorite: false, isMuted: false, isHidden: false };
          if (newCh.type === "dm") {
            const otherM = others[0];
            if (otherM?.user) enriched.dmUser = { id: otherM.user.id, name: otherM.user.name, companyId: otherM.user.companyId, profileImage: otherM.user.profileImage, online: otherM.user.online, statusEmoji: otherM.user.statusEmoji, statusText: otherM.user.statusText };
          } else if (newCh.type === "group_dm") {
            enriched.groupMembers = others.map(m => ({ id: m.user.id, name: m.user.name, companyId: m.user.companyId, profileImage: m.user.profileImage, online: m.user.online, statusEmoji: m.user.statusEmoji, statusText: m.user.statusText }));
          }
          return [...prev, enriched].sort((a, b) => {
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;
            if (!a.lastMessageAt) return 1; if (!b.lastMessageAt) return -1;
            return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
          });
        });
        return;
      }

      // ============ Real-time message events (entregues por user-{id}) ============
      if (data.action === "newInternalMessage") {
        // Fallback: se canal desconhecido, refetch lista toda
        setChannels(prev => {
          const known = prev.some(c => c.id === data.channelId);
          if (!known) { fetchChannels(); return prev; }
          return prev;
        });
        // bump da lista de canais (lastMessage / lastMessageAt) E sort
        setChannels(prev => {
          const updated = prev.map(ch => ch.id === data.channelId ? {
            ...ch,
            lastMessage: (data.message?.body || "").substring(0, 200),
            lastMessageAt: data.message?.createdAt || new Date().toISOString(),
            unreadCount: ch.id === selectedChannelId ? 0 : ((ch.unreadCount || 0) + (data.message?.senderId !== user.id ? 1 : 0))
          } : ch);
          return updated.sort((a, b) => {
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;
            if (!a.lastMessageAt) return 1; if (!b.lastMessageAt) return -1;
            return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
          });
        });
        // se canal atual, anexar msg na lista
        if (data.channelId === selectedChannelId) {
          setMessages(prev => {
            if (prev.some(m => m.id === data.message.id)) return prev;
            const enriched = data.message?.sender?.id === user.id
              ? { ...data.message, sender: { ...data.message.sender, online: true } }
              : data.message;
            return [...prev, enriched];
          });
          api.post(`/internal-chat/channels/${selectedChannelId}/read`).catch(() => {});
        }
        return;
      }
      if (data.action === "updateInternalMessage" && data.channelId === selectedChannelId) {
        setMessages(prev => prev.map(m => m.id === data.message.id
          ? { ...m, ...data.message, sender: (data.message?.sender?.company ? data.message.sender : m.sender) }
          : m));
        return;
      }
      if (data.action === "deleteInternalMessage" && data.channelId === selectedChannelId) {
        setMessages(prev => prev.filter(m => m.id !== data.messageId));
        return;
      }
      if (data.action === "reactionInternalMessage" && data.channelId === selectedChannelId) {
        setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
        return;
      }
      // ============ Mention notification (original) ============
      if (data.action === "internalChatMention") {
        if (data.channelId !== selectedChannelId) {
          setMentionedChannels(prev => new Set([...prev, data.channelId]));
        }
        if (data.channelId === selectedChannelId) return;
        setMentionToast({
          senderName: data.senderDisplayName || data.senderName,
          body: data.body,
          channelId: data.channelId,
        });
        playMentionSound();
        // Notificação nativa do browser é tratada globalmente pelo hook
        // useInternalChatNotifications (frontend/src/hooks/...) montado no
        // LoggedInLayout — funciona mesmo com a página /internal-chat fechada.
      }
    };
    socket.on(`user-${user.id}`, handleMentionNotification);
    return () => { socket.off(`user-${user.id}`, handleMentionNotification); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketManager, user.companyId, user.id, selectedChannelId, playMentionSound]);

  // ── Send ──
  // Slash commands
  const processSlashCommands = (raw) => {
    const t = raw.trim();
    if (!t.startsWith("/")) return raw;
    if (t === "/shrug") return "¯\\_(ツ)_/¯";
    if (t === "/tableflip") return "(╯°□°)╯︵ ┻━┻";
    if (t === "/unflip") return "┬─┬ノ(°-°ノ)";
    if (t.startsWith("/me ")) return "_" + t.substring(4) + "_";
    return raw;
  };

  const handleSendMessage = async () => {
    if ((!messageText.trim() && !attachedFile) || !selectedChannelId) return;
    const text = processSlashCommands(messageText), file = attachedFile, reply = replyingTo;
    const mentionIds = mentionedUsers
      .filter(mu => text.includes("@" + mu.name))
      .map(mu => mu.id);
    setMessageText(""); setAttachedFile(null); setMentionedUsers([]); setShowMentions(false); setReplyingTo(null);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("body", text || "");
        fd.append("medias", file);
        if (mentionIds.length > 0) fd.append("mentions", JSON.stringify(mentionIds));
        if (reply) fd.append("parentMessageId", String(reply.id));
        await api.post(`/internal-chat/channels/${selectedChannelId}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const payload = { body: text };
        if (mentionIds.length > 0) payload.mentions = JSON.stringify(mentionIds);
        if (reply) payload.parentMessageId = reply.id;
        await api.post(`/internal-chat/channels/${selectedChannelId}/messages`, payload);
      }
    } catch (err) { toastError(err); setMessageText(text); setAttachedFile(file); setReplyingTo(reply); }
  };

  // ── Actions ──
  const handleSelectChannel = (id) => {
    setSelectedChannelId(id); setShowMobileSidebar(false); setMentionedUsers([]); setShowMentions(false); setReplyingTo(null); setThreadParentMsg(null); setThreadText(""); setMsgSearchOpen(false); setMsgSearchQuery("");
    setMentionedChannels(prev => { const next = new Set(prev); next.delete(id); return next; });
    history.push(`/internal-chat/${id}`);
  };
  const handleOpenBrowser = async () => {
    setChannelBrowserOpen(true);
    setLoadingBrowser(true);
    try {
      const { data } = await api.get("/internal-chat/channels/browse");
      setAvailableChannels(data);
    } catch (err) { toastError(err); }
    finally { setLoadingBrowser(false); }
  };
  const handleJoinChannel = async (channelId) => {
    try {
      await api.post(`/internal-chat/channels/${channelId}/join`);
      setAvailableChannels(prev => prev.filter(c => c.id !== channelId));
      fetchChannels();
      handleSelectChannel(channelId);
      setChannelBrowserOpen(false);
    } catch (err) { toastError(err); }
  };
  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) return;
    try {
      const { data } = await api.post("/internal-chat/channels", { name: newChannelName.trim(), description: newChannelDesc.trim() || undefined });
      setCreateChannelOpen(false); setNewChannelName(""); setNewChannelDesc(""); fetchChannels(); handleSelectChannel(data.id);
    } catch (err) { toastError(err); }
  };
  const handleCreateDM = async (targetUserId) => {
    try {
      const { data } = await api.post("/internal-chat/dm", { targetUserId });
      setNewDMOpen(false); setDmSearch(""); setDmSelected([]);
      // Aguarda o refetch antes de selecionar: garante que o canal já esteja
      // em `channels` quando setSelectedChannelId disparar, evitando flash do
      // empty state caso o socket newInternalChannel atrase.
      await fetchChannels();
      handleSelectChannel(data.id);
    } catch (err) { toastError(err); }
  };
  const handleCreateGroupDM = async (memberIds) => {
    try {
      const { data } = await api.post("/internal-chat/group-dm", { memberIds });
      setNewDMOpen(false); setDmSearch(""); setDmSelected([]);
      await fetchChannels();
      handleSelectChannel(data.id);
    } catch (err) { toastError(err); }
  };
  const handleSubmitNewConversation = () => {
    if (dmSelected.length === 0) return;
    if (dmSelected.length === 1) handleCreateDM(dmSelected[0].id);
    else handleCreateGroupDM(dmSelected.map(u => u.id));
  };
  const toggleDmSelect = (u) => {
    setDmSelected(prev => {
      if (prev.some(x => x.id === u.id)) return prev.filter(x => x.id !== u.id);
      return [...prev, u];
    });
  };
  const handleToggleFavoriteChannel = async (ch) => {
    try {
      await api.patch(`/internal-chat/channels/${ch.id}/membership`, { isFavorite: !ch.isFavorite });
      setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, isFavorite: !ch.isFavorite } : c));
    } catch (err) { toastError(err); }
    setChannelMenuAnchor(null);
  };
  const handleToggleMuteChannel = async (ch) => {
    try {
      await api.patch(`/internal-chat/channels/${ch.id}/membership`, { isMuted: !ch.isMuted });
      setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, isMuted: !ch.isMuted } : c));
    } catch (err) { toastError(err); }
    setChannelMenuAnchor(null);
  };
  const handleCloseConversation = async (ch) => {
    if (ch.isDefault) { setChannelMenuAnchor(null); return; }
    try {
      await api.post(`/internal-chat/channels/${ch.id}/close`);
      setChannels(prev => prev.filter(c => c.id !== ch.id));
      if (selectedChannelId === ch.id) { setSelectedChannelId(null); setMessages([]); history.push("/internal-chat"); }
    } catch (err) { toastError(err); }
    setChannelMenuAnchor(null);
  };
  const handleMarkUnread = async (ch) => {
    try {
      const firstUnreadFromBottom = selectedChannelId === ch.id && messages.length > 0
        ? messages[messages.length - 1].id
        : 0;
      await api.post(`/internal-chat/channels/${ch.id}/unread`, { fromMessageId: firstUnreadFromBottom });
      setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c));
    } catch (err) { toastError(err); }
    setChannelMenuAnchor(null);
  };
  const handleMarkRead = async (ch) => {
    try {
      await api.post(`/internal-chat/channels/${ch.id}/read`);
      setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, unreadCount: 0 } : c));
    } catch (err) { toastError(err); }
    setChannelMenuAnchor(null);
  };
  const openChannelMenu = (e, ch) => {
    e.preventDefault(); e.stopPropagation();
    setChannelMenuAnchor(e.currentTarget);
    setChannelMenuTarget(ch);
  };
  const handleTogglePin = async (msg) => {
    try {
      const { data } = await api.post(`/internal-chat/messages/${msg.id}/pin`);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isPinned: data.isPinned } : m));
    } catch (err) { toastError(err); }
  };
  const handleToggleSave = async (msg) => {
    try {
      const { data } = await api.post(`/internal-chat/messages/${msg.id}/save`);
      setSavedMessageIds(prev => {
        const next = new Set(prev);
        if (data.saved) next.add(msg.id); else next.delete(msg.id);
        return next;
      });
    } catch (err) { toastError(err); }
  };
  const openPanel = async (kind) => {
    if (activePanel === kind) { setActivePanel(null); return; }
    setActivePanel(kind); setLoadingPanel(true);
    try {
      let url;
      if (kind === "pinned") url = `/internal-chat/channels/${selectedChannelId}/pinned`;
      else if (kind === "saved") url = "/internal-chat/saved";
      else if (kind === "mentions") url = "/internal-chat/mentions";
      const { data } = await api.get(url);
      setPanelMessages(data);
    } catch (err) { toastError(err); }
    finally { setLoadingPanel(false); }
  };
  const runGlobalSearch = async (q) => {
    if (!q || q.trim().length < 2) { setGlobalSearchResults([]); return; }
    setLoadingGlobalSearch(true);
    try {
      const { data } = await api.get(`/internal-chat/search?q=${encodeURIComponent(q.trim())}`);
      setGlobalSearchResults(data);
    } catch (err) { /* ignore */ }
    finally { setLoadingGlobalSearch(false); }
  };
  const handleAddEmoji = (e) => {
    const emoji = e.native || e.shortcodes || "";
    setMessageText(prev => prev + emoji);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };
  const handlePickReactionEmoji = (e) => {
    const emoji = e.native || "";
    if (reactionPickerMsgId && emoji) {
      handleReaction(reactionPickerMsgId, emoji);
    }
    setReactionPickerMsgId(null);
  };

  const openProfileCard = (u, e) => {
    if (!u || !u.id) return;
    if (u.id === user.id) return; // não abre pra mim mesmo
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    const target = e?.currentTarget || e?.target;
    const rect = target?.getBoundingClientRect?.() || { right: window.innerWidth / 2, left: window.innerWidth / 2, top: window.innerHeight / 2 };
    // Abre à direita do elemento; se não couber (ex.: lista de membros na borda
    // direita), faz flip para a esquerda do item — assim não tapa o que clicou.
    const CARD_W = 248;
    const GAP = 8;
    let x = rect.right + GAP;
    if (x + CARD_W > window.innerWidth - 8) x = rect.left - CARD_W - GAP;
    if (x < 8) x = 8;
    setProfileCard({ user: u, x, y: rect.top });
  };
  const handleProfileCardDM = async () => {
    if (!profileCard) return;
    const target = profileCard.user;
    setProfileCard(null);
    try {
      const { data } = await api.post("/internal-chat/dm", { targetUserId: target.id });
      fetchChannels();
      handleSelectChannel(data.id);
    } catch (err) { toastError(err); }
  };

  const handleOpenInvite = async (chParam) => {
    const ch = chParam || selectedChannel;
    if (!ch) return;
    try {
      const { data } = await api.get(`/internal-chat/channels/${ch.id}/invite`);
      const url = `${window.location.origin}/internal-chat/invite/${data.token}`;
      setInviteLink(url);
      setInviteCopied(false);
    } catch (err) { toastError(err); }
  };
  const handleCopyInvite = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };
  const handleRotateInvite = async () => {
    // Pega o id do channel a partir do inviteLink atual (já está aberto)
    // ou do selectedChannel
    if (!selectedChannel) return;
    try {
      const { data } = await api.post(`/internal-chat/channels/${selectedChannel.id}/invite/rotate`);
      const url = `${window.location.origin}/internal-chat/invite/${data.token}`;
      setInviteLink(url);
      setInviteCopied(false);
    } catch (err) { toastError(err); }
  };

  const handleOpenRename = () => {
    if (!selectedChannel) return;
    setRenameInput(selectedChannel.customName || "");
    setRenameDialogOpen(true);
  };
  const handleSubmitRename = async () => {
    if (!selectedChannel) return;
    try {
      await api.patch(`/internal-chat/channels/${selectedChannel.id}/rename`, { customName: renameInput.trim() });
      setChannels(prev => prev.map(c => c.id === selectedChannel.id ? { ...c, customName: renameInput.trim() || null } : c));
      setRenameDialogOpen(false);
    } catch (err) { toastError(err); }
  };
  const handleSetStatus = async () => {
    try {
      const { data } = await api.post("/internal-chat/status", { emoji: statusEmojiInput, text: statusTextInput });
      // Atualiza estado local imediatamente
      setMyStatus({ emoji: data.statusEmoji || "", text: data.statusText || "" });
      // Propaga em msgs locais
      setMessages(prev => prev.map(m => m.sender?.id === user.id
        ? { ...m, sender: { ...m.sender, statusEmoji: data.statusEmoji, statusText: data.statusText } }
        : m));
      setStatusDialogOpen(false);
    } catch (err) { toastError(err); }
  };
  const handleReaction = async (messageId, emoji) => { try { await api.post(`/internal-chat/messages/${messageId}/reactions`, { emoji }); } catch (err) { /* socket */ } };
  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm("Tem certeza que deseja excluir este canal? Todas as mensagens serao perdidas.")) return;
    try { await api.delete(`/internal-chat/channels/${channelId}`); setSelectedChannelId(null); setMessages([]); fetchChannels(); history.push("/internal-chat"); }
    catch (err) { toastError(err); }
  };
  const handleDeleteMessage = async (messageId) => { try { await api.delete(`/internal-chat/messages/${messageId}`); } catch (err) { toastError(err); } };
  const handleStartEdit = (msg) => { setEditingMessageId(msg.id); setEditingMessageText(msg.body || ""); };
  const handleCancelEdit = () => { setEditingMessageId(null); setEditingMessageText(""); };
  const handleSaveEdit = async () => {
    if (!editingMessageId) return;
    const newBody = editingMessageText.trim();
    if (!newBody) return;
    try {
      const { data } = await api.put(`/internal-chat/messages/${editingMessageId}`, { body: newBody });
      // update imediato preservando o sender/company da mensagem atual em caso de payload reduzido
      setMessages(prev => prev.map(m => {
        if (m.id !== editingMessageId) return m;
        return { ...m, ...data, sender: (data?.sender?.company ? data.sender : m.sender) };
      }));
      setEditingMessageId(null);
      setEditingMessageText("");
    } catch (err) { toastError(err); }
  };
  const closeThread = () => { setThreadParentMsg(null); setThreadText(""); };
  const handleSendThread = async () => {
    if (!threadText.trim() || !threadParentMsg || !selectedChannelId) return;
    const text = threadText, parent = threadParentMsg;
    setThreadText("");
    try {
      await api.post(`/internal-chat/channels/${selectedChannelId}/messages`, { body: text, parentMessageId: parent.id });
    } catch (err) { toastError(err); setThreadText(text); }
  };

  // ── Members ──
  const fetchMembers = useCallback(async (chId) => {
    if (!chId) return; setLoadingMembers(true);
    try { const { data } = await api.get(`/internal-chat/channels/${chId}/members`); setChannelMembers(data); }
    catch (err) { toastError(err); } finally { setLoadingMembers(false); }
  }, []);
  const handleToggleMembers = () => { if (!showMembers && selectedChannelId) fetchMembers(selectedChannelId); setMemberSearch(""); setShowMembers(p => !p); };
  const handleAddMember = async (targetUserId) => {
    if (!selectedChannelId) return;
    try { await api.post(`/internal-chat/channels/${selectedChannelId}/members`, { userId: targetUserId }); fetchMembers(selectedChannelId); setAddMemberDialogOpen(false); setAddMemberSearch(""); }
    catch (err) { toastError(err); }
  };
  const handleRemoveMember = async (targetUserId) => {
    if (!selectedChannelId) return;
    try { await api.delete(`/internal-chat/channels/${selectedChannelId}/members/${targetUserId}`); fetchMembers(selectedChannelId); }
    catch (err) { toastError(err); }
  };
  // Carregar membros para @mencoes — só quando o painel está aberto ou ao mudar de canal com painel visível
  useEffect(() => {
    if (selectedChannelId && showMembers) fetchMembers(selectedChannelId);
    setMemberSearch(""); // troca de canal zera o filtro de busca de membros
  }, [selectedChannelId]); // eslint-disable-line

  // ── Derived ──
  const selectedChannel = channels.find(ch => ch.id === selectedChannelId);
  const getChannelRawName = (ch) => (ch.type === "dm" && ch.dmUser) ? getInternalChatUserName(ch.dmUser) : ch.name;
  const channelDisplayName = (ch) => {
    if (ch.customName) return ch.customName;
    if (ch.type === "dm" && ch.dmUser) return getInternalChatDisplayName(ch.dmUser);
    if (ch.type === "group_dm") return (ch.groupMembers || []).map(m => m.name.split(" ")[0]).join(", ") || "Grupo";
    return ch.name;
  }
  const filteredChannels = channels.filter(ch => !searchTerm || getChannelRawName(ch).toLowerCase().includes(searchTerm.toLowerCase()));
  const regularChannels = filteredChannels.filter(ch => ch.type === "channel" && !ch.isFavorite);
  const dmChannels = filteredChannels.filter(ch => (ch.type === "dm" || ch.type === "group_dm") && !ch.isFavorite);
  const favoriteChannels = filteredChannels.filter(ch => ch.isFavorite);
  const memberIds = channelMembers.map(m => m.user?.id || m.userId);
  const nonMembers = companyUsers.filter(u => !memberIds.includes(u.id));
  const filteredNonMembers = addMemberSearch ? nonMembers.filter(u => u.name.toLowerCase().includes(addMemberSearch.toLowerCase())) : nonMembers;
  const filteredDmUsers = dmSearch ? companyUsers.filter(u => u.name.toLowerCase().includes(dmSearch.toLowerCase())) : companyUsers;

  // ── SIDEBAR ──
  const renderSidebar = () => (
    <div className={classes.sidebar}>
      <div className={classes.sidebarHeader}>
        <div className={classes.teamName}>{groupName || "Chat Interno"}</div>
        <div className={classes.teamSub}>Equipe</div>
        <button
          onClick={() => { setStatusEmojiInput(myStatus.emoji || ""); setStatusTextInput(myStatus.text || ""); setStatusDialogOpen(true); }}
          style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, background: "transparent", cursor: "pointer", color: myStatus.text ? (dark ? "#FAFAFA" : "#09090B") : (dark ? "#A1A1AA" : "#71717A"), fontSize: 12, width: "100%", textAlign: "left" }}
          title="Definir status">
          {myStatus.emoji ? <span style={{ fontSize: 14 }}>{myStatus.emoji}</span> : <Smile size={14} />}
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {myStatus.text || "Definir status"}
          </span>
        </button>
      </div>

      <div className={classes.sidebarSearch} style={{ display: "flex", gap: 6 }}>
        <TextField
          placeholder="Buscar conversa..."
          size="small"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchInput}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={16} style={{ color: dark ? "#71717A" : "#A1A1AA" }} /></InputAdornment>,
          }}
        />
        <Tooltip title="Busca global (Ctrl+K)">
          <IconButton size="small" onClick={() => setGlobalSearchOpen(true)} style={{ padding: 6 }}>
            <Search size={16} />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.channelList}>
        {/* FAVORITOS */}
        {favoriteChannels.length > 0 && (
          <>
            <div className={classes.sectionHeader} style={{ marginTop: 0 }}>
              <Star size={11} style={{ color: "#F59E0B", marginRight: 2 }} fill="#F59E0B" />
              <span className={classes.sectionLabel} style={{ marginLeft: 2 }}>Favoritos</span>
            </div>
            {favoriteChannels.map(ch => {
              const isActive = ch.id === selectedChannelId;
              const hasUnread = ch.unreadCount > 0 && !ch.isMuted;
              const hasMention = mentionedChannels.has(ch.id);
              const isDM = ch.type === "dm" || ch.type === "group_dm";
              const rawDmName = ch.dmUser?.name || ch.name;
              const displayName = isDM ? (ch.dmUser ? getInternalChatDisplayName(ch.dmUser) : ch.name) : ch.name;
              return (
                <div key={`fav-${ch.id}`}
                  className={`${classes.channelItem} ${isActive ? classes.channelItemActive : ""} ${hasUnread ? classes.channelItemUnread : ""}`}
                  onClick={() => handleSelectChannel(ch.id)}
                  onContextMenu={(e) => openChannelMenu(e, ch)}>
                  {hasMention ? <div className={classes.mentionAtBadge}>@</div> : hasUnread ? <div className={classes.unreadDot} /> : null}
                  {isDM ? (
                    <div className={classes.dmAvatarWrapper}>
                      <AvatarEl name={rawDmName} profileImage={ch.dmUser?.profileImage} size={24} fontSize={9} className={classes.dmAvatar} />
                      <span className={`${classes.statusDot} ${ch.dmUser?.online ? classes.statusDotOnline : classes.statusDotOffline}`} />
                    </div>
                  ) : (
                    <span className={classes.channelHash}>#</span>
                  )}
                  <span className={classes.channelName} style={{ flex: 1 }}>{displayName}</span>
                  {ch.isMuted && <BellOff size={11} style={{ opacity: 0.5, marginRight: 4 }} />}
                  <IconButton size="small" className={classes.channelMenuBtn} onClick={(e) => openChannelMenu(e, ch)}>
                    <MoreVertical size={13} />
                  </IconButton>
                </div>
              );
            })}
          </>
        )}

        {/* CANAIS */}
        <div className={classes.sectionHeader} onClick={() => setChannelsOpen(!channelsOpen)} style={{ marginTop: favoriteChannels.length > 0 ? 8 : 0 }}>
          <ChevronDown className={classes.sectionToggle} style={!channelsOpen ? { transform: "rotate(-90deg)" } : {}} />
          <span className={classes.sectionLabel}>Canais</span>
          {!channelsOpen && regularChannels.some(ch => ch.unreadCount > 0) && (
            <span className={classes.unreadBadge} style={{ fontSize: 9, padding: "1px 5px", marginRight: 4 }}>
              {regularChannels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0)}
            </span>
          )}
          {(user.profile === "admin" || user.super) && (
            <Tooltip title="Criar canal">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCreateChannelOpen(true); }}
                style={{ color: dark ? "#71717A" : "#A1A1AA", padding: 2 }}>
                <Plus size={15} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {channelsOpen && (
          <>
            {regularChannels.map(ch => {
              const isActive = ch.id === selectedChannelId;
              const hasUnread = ch.unreadCount > 0 && !ch.isMuted;
              const hasMention = mentionedChannels.has(ch.id);
              return (
                <div key={ch.id}
                  className={`${classes.channelItem} ${isActive ? classes.channelItemActive : ""} ${hasUnread ? classes.channelItemUnread : ""}`}
                  onClick={() => handleSelectChannel(ch.id)}
                  onContextMenu={(e) => openChannelMenu(e, ch)}>
                  {hasMention ? <div className={classes.mentionAtBadge}>@</div> : hasUnread ? <div className={classes.unreadDot} /> : null}
                  <span className={classes.channelHash}>#</span>
                  <span className={classes.channelName} style={{ flex: 1 }}>{ch.name}</span>
                  {ch.isMuted && <BellOff size={11} style={{ opacity: 0.5, marginRight: 4 }} />}
                  <IconButton size="small" className={classes.channelMenuBtn} onClick={(e) => openChannelMenu(e, ch)}>
                    <MoreVertical size={13} />
                  </IconButton>
                </div>
              );
            })}
            {(user.profile === "admin" || user.super) && (
              <>
                <div className={classes.addChannelBtn} onClick={handleOpenBrowser}>
                  <Search size={15} />
                  <span>Procurar canais</span>
                </div>
                <div className={classes.addChannelBtn} onClick={() => setCreateChannelOpen(true)}>
                  <Plus size={15} />
                  <span>Criar canal</span>
                </div>
              </>
            )}
          </>
        )}

        {/* DMs */}
        <div className={classes.sectionHeader} onClick={() => setDmsOpen(!dmsOpen)} style={{ marginTop: 8 }}>
          <ChevronDown className={classes.sectionToggle} style={!dmsOpen ? { transform: "rotate(-90deg)" } : {}} />
          <span className={classes.sectionLabel}>Mensagens Diretas</span>
          {!dmsOpen && dmChannels.some(ch => ch.unreadCount > 0) && (
            <span className={classes.unreadBadge} style={{ fontSize: 9, padding: "1px 5px", marginRight: 4 }}>
              {dmChannels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0)}
            </span>
          )}
          <Tooltip title="Nova conversa">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNewDMOpen(true); setDmSearch(""); setDmSelected([]); }}
              style={{ color: dark ? "#71717A" : "#A1A1AA", padding: 2 }}>
              <Plus size={15} />
            </IconButton>
          </Tooltip>
        </div>

        {dmsOpen && dmChannels.map(ch => {
          const isGroupDm = ch.type === "group_dm";
          const rawDmName = ch.customName ||
            (isGroupDm
              ? (ch.groupMembers || []).map(m => m.name).join(", ") || "Grupo"
              : (ch.dmUser?.name || ch.name));
          const displayName = ch.customName ||
            (isGroupDm
              ? (ch.groupMembers || []).map(m => m.name.split(" ")[0]).join(", ") || "Grupo"
              : (ch.dmUser ? getInternalChatDisplayName(ch.dmUser) : ch.name));
          const isActive = ch.id === selectedChannelId;
          const hasUnread = ch.unreadCount > 0 && !ch.isMuted;
          const hasMention = mentionedChannels.has(ch.id);
          const dmProfileImage = ch.dmUser?.profileImage;
          const dmIsOnline = ch.dmUser?.online;
          return (
            <div key={ch.id}
              className={`${classes.channelItem} ${isActive ? classes.channelItemActive : ""} ${hasUnread ? classes.channelItemUnread : ""}`}
              onClick={() => handleSelectChannel(ch.id)}
              onContextMenu={(e) => openChannelMenu(e, ch)}>
              {isGroupDm ? (
                <div style={{ position: "relative", width: 32, height: 24, flexShrink: 0 }}>
                  {(ch.groupMembers || []).slice(0, 2).map((m, i) => (
                    <div key={m.id} style={{ position: "absolute", top: i === 0 ? 0 : 6, left: i === 0 ? 0 : 10 }}>
                      <AvatarEl name={m.name} profileImage={m.profileImage} size={18} fontSize={8} className={classes.dmAvatar} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={classes.dmAvatarWrapper}>
                  <AvatarEl name={rawDmName} profileImage={dmProfileImage} size={24} fontSize={9} className={classes.dmAvatar} />
                  <span className={`${classes.statusDot} ${dmIsOnline ? classes.statusDotOnline : classes.statusDotOffline}`} />
                </div>
              )}
              <span className={classes.channelName} style={{ flex: 1 }}>{displayName}</span>
              {ch.isMuted && <BellOff size={11} style={{ opacity: 0.5, marginRight: 4 }} />}
              {hasMention ? <div className={classes.mentionAtBadge}>@</div> : hasUnread ? <div className={classes.unreadDot} /> : null}
              <IconButton size="small" className={classes.channelMenuBtn} onClick={(e) => openChannelMenu(e, ch)}>
                <MoreVertical size={13} />
              </IconButton>
            </div>
          );
        })}

        {loading && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CircularProgress size={20} style={{ color: accent }} />
          </div>
        )}
      </div>
    </div>
  );

  // ── CHANNEL CONTEXT MENU (padrão do sistema com seções + ícones em caixinha) ──
  const renderChannelMenu = () => {
    const ch = channelMenuTarget;
    if (!ch) return null;
    const isDM = ch.type === "dm" || ch.type === "group_dm";
    const accent = dark ? "#5B7CFF" : "#3D5CFD";
    const close = () => setChannelMenuAnchor(null);
    return (
      <Menu
        anchorEl={channelMenuAnchor}
        open={Boolean(channelMenuAnchor)}
        onClose={close}
        PaperProps={{ className: classes.menuPaper }}
      >
        <div className={classes.menuSectionLabel}>Leitura</div>
        {(ch.unreadCount > 0) ? (
          <div className={classes.menuItemStyled} onClick={() => handleMarkRead(ch)}>
            <div className={classes.menuItemIconBox} style={{ background: `${accent}22` }}>
              <CheckCheck size={16} color={accent} />
            </div>
            <span className={classes.menuItemText}>Marcar como lida</span>
          </div>
        ) : (
          <div className={classes.menuItemStyled} onClick={() => handleMarkUnread(ch)}>
            <div className={classes.menuItemIconBox} style={{ background: `${accent}22` }}>
              <Hash size={16} color={accent} />
            </div>
            <span className={classes.menuItemText}>Marcar como não lida</span>
          </div>
        )}

        <div className={classes.menuSectionLabel}>Organizar</div>
        <div className={classes.menuItemStyled} onClick={() => handleToggleFavoriteChannel(ch)}>
          <div className={classes.menuItemIconBox} style={{ background: "rgba(245,158,11,0.18)" }}>
            <Star size={16} color="#F59E0B" fill={ch.isFavorite ? "#F59E0B" : "none"} />
          </div>
          <span className={classes.menuItemText}>{ch.isFavorite ? "Desfavoritar" : "Favoritar"}</span>
        </div>
        <div className={classes.menuItemStyled} onClick={() => handleToggleMuteChannel(ch)}>
          <div className={classes.menuItemIconBox} style={{ background: "rgba(168,85,247,0.18)" }}>
            {ch.isMuted ? <Bell size={16} color="#A855F7" /> : <BellOff size={16} color="#A855F7" />}
          </div>
          <span className={classes.menuItemText}>{ch.isMuted ? "Reativar notificações" : "Silenciar conversa"}</span>
        </div>

        {ch.type === "channel" && !ch.isDefault && (
          <>
            <div className={classes.menuSectionLabel}>Compartilhar</div>
            <div className={classes.menuItemStyled} onClick={() => { close(); handleOpenInvite(ch); }}>
              <div className={classes.menuItemIconBox} style={{ background: "rgba(16,185,129,0.18)" }}>
                <UserPlus size={16} color="#10B981" />
              </div>
              <span className={classes.menuItemText}>Copiar link de convite</span>
            </div>
          </>
        )}

        {!ch.isDefault && (
          <>
            <div className={classes.menuSectionLabel}>Ações</div>
            <div className={`${classes.menuItemStyled} ${classes.menuItemDestructive}`} onClick={() => handleCloseConversation(ch)}>
              <div className={classes.menuItemIconBox} style={{ background: "rgba(239,68,68,0.15)" }}>
                <X size={16} color="#EF4444" />
              </div>
              <span className={classes.menuItemText} style={{ color: "#EF4444" }}>
                {isDM ? "Fechar conversa" : "Sair do canal"}
              </span>
            </div>
          </>
        )}
      </Menu>
    );
  };

  // ── MESSAGES ──
  const renderMessages = () => {
    if (!selectedChannel) {
      return (
        <div className={classes.emptyState}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: `linear-gradient(135deg, ${accent}20, ${accent}08)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 8,
          }}>
            <TagIcon style={{ fontSize: 28, color: accent }} />
          </div>
          <Typography style={{ fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" }}>
            Chat Interno
          </Typography>
          <Typography style={{ fontSize: 14, color: dark ? "#71717A" : "#A1A1AA", maxWidth: 280, textAlign: "center", lineHeight: 1.5 }}>
            Selecione um canal ou conversa para começar a colaborar com a equipe
          </Typography>
        </div>
      );
    }

    const memberCount = selectedChannel.memberCount || channelMembers.length || 0;
    let lastDate = null;

    return (
      <>
        {/* Header */}
        <div className={classes.chatHeader}>
          {isMobile && (
            <IconButton size="small" onClick={() => setShowMobileSidebar(true)} style={{ marginRight: 4 }}>
              <ArrowLeft size={18} />
            </IconButton>
          )}
          {selectedChannel.type === "channel" && (
            <span className={classes.chatHeaderHash}>#</span>
          )}
          <span className={classes.chatHeaderName}>{channelDisplayName(selectedChannel)}</span>
          {(selectedChannel.type === "group_dm" || (selectedChannel.type === "channel" && !selectedChannel.isDefault && (user.profile === "admin" || user.super))) && (
            <Tooltip title="Renomear conversa">
              <IconButton size="small" onClick={handleOpenRename} style={{ padding: 4, marginLeft: 4, opacity: 0.5 }}>
                <Pencil size={13} />
              </IconButton>
            </Tooltip>
          )}


          {selectedChannel.type === "dm" && (() => {
            const isOnline = selectedChannel.dmUser?.online;
            return (
              <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: isOnline ? "#22C55E" : dark ? "#71717A" : "#A1A1AA" }}>
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: isOnline ? "#22C55E" : "#EF4444",
                  display: "inline-block", flexShrink: 0,
                  boxShadow: isOnline ? "0 0 6px #22C55E80" : "0 0 6px #EF444480",
                }} />
                {isOnline ? "online" : "offline"}
              </span>
            );
          })()}

          {selectedChannel.type !== "dm" && (
            <div className={classes.chatHeaderMeta} onClick={handleToggleMembers}>
              <Users size={14} />
              <span>{memberCount || ""}</span>
            </div>
          )}

          {selectedChannel.topic && (
            <span style={{
              fontSize: 13, color: dark ? "#71717A" : "#A1A1AA",
              flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginLeft: 8,
            }}>
              {selectedChannel.topic}
            </span>
          )}

          <div className={classes.chatHeaderActions}>
            <Tooltip title="Mensagens fixadas no canal">
              <IconButton className={classes.chatHeaderBtn} size="small" onClick={() => openPanel("pinned")}
                style={activePanel === "pinned" ? { color: "#F59E0B", background: "rgba(245,158,11,0.12)" } : {}}>
                <Pin size={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mensagens salvas">
              <IconButton className={classes.chatHeaderBtn} size="small" onClick={() => openPanel("saved")}
                style={activePanel === "saved" ? { color: dark ? "#5B7CFF" : "#3D5CFD", background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)" } : {}}>
                <Bookmark size={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Menções recentes">
              <IconButton className={classes.chatHeaderBtn} size="small" onClick={() => openPanel("mentions")}
                style={activePanel === "mentions" ? { color: dark ? "#5B7CFF" : "#3D5CFD", background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)" } : {}}>
                <AtSign size={17} />
              </IconButton>
            </Tooltip>
            {msgSearchOpen ? (
              <TextField
                autoFocus
                size="small"
                variant="outlined"
                placeholder="Buscar mensagens..."
                value={msgSearchQuery}
                onChange={(e) => setMsgSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setMsgSearchOpen(false); setMsgSearchQuery(""); } }}
                style={{ width: 220, marginRight: 6 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search size={14} style={{ opacity: 0.5 }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setMsgSearchOpen(false); setMsgSearchQuery(""); }} style={{ padding: 2 }}>
                        <X size={13} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Tooltip title="Buscar mensagens (Ctrl+F)">
                <IconButton className={classes.chatHeaderBtn} size="small" onClick={() => setMsgSearchOpen(true)}>
                  <Search size={17} />
                </IconButton>
              </Tooltip>
            )}
            {user.profile === "admin" && selectedChannel.type === "channel" && !selectedChannel.isDefault && (
              <Tooltip title="Excluir canal">
                <IconButton className={classes.deleteChannelBtn} size="small" onClick={() => handleDeleteChannel(selectedChannel.id)}>
                  <Trash2 size={17} />
                </IconButton>
              </Tooltip>
            )}
            {selectedChannel.type !== "dm" && (
              <Tooltip title="Membros">
                <IconButton className={classes.chatHeaderBtn} size="small" onClick={handleToggleMembers}
                  style={showMembers ? { color: dark ? "#5B7CFF" : "#3D5CFD", background: `${accent}12` } : {}}>
                  <Users size={17} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Messages */}
        <div
          className={classes.messageList}
          ref={messageListRef}
          onScroll={(e) => {
            // Trigger scroll reverso quando user chega perto do topo.
            if (e.currentTarget.scrollTop < 80 && messagesHasMore && !loadingOlderMessages) {
              loadOlderMessages();
            }
          }}
        >
          {loadingOlderMessages && (
            <div style={{ textAlign: "center", padding: 12 }}>
              <CircularProgress size={18} style={{ color: accent }} />
            </div>
          )}
          {loadingMessages && (
            <div style={{ textAlign: "center", padding: 48 }}>
              <CircularProgress size={24} style={{ color: accent }} />
            </div>
          )}
          {!loadingMessages && messages.length === 0 && (
            <div className={classes.emptyState}>
              <Typography style={{ fontSize: 14, color: dark ? "#71717A" : "#A1A1AA" }}>
                Nenhuma mensagem ainda. Seja o primeiro a enviar!
              </Typography>
            </div>
          )}

          {visibleMessages.map((msg, idx, arr) => {
            const msgDate = formatDateSep(msg.createdAt);
            const showDate = msgDate !== lastDate;
            lastDate = msgDate;
            const prevMsg = idx > 0 ? arr[idx - 1] : null;
            const sameAuthor = prevMsg && prevMsg.senderId === msg.senderId && !showDate;
            const timeDiff = prevMsg ? (new Date(msg.createdAt) - new Date(prevMsg.createdAt)) / 60000 : 999;
            const grouped = sameAuthor && timeDiff < 5;
            const senderName = getInternalChatUserName(msg.sender);
            const senderDisplayName = getInternalChatDisplayName(msg.sender);
            const senderCompany = groupName && msg.sender?.company?.name;

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className={classes.dateSep}>
                    <span className={classes.dateSepText}>{msgDate}</span>
                  </div>
                )}
                {msg.id === unreadDividerMsgId && (
                  <div className={classes.unreadSep}>
                    <span className={classes.unreadSepText}>Novas Mensagens</span>
                  </div>
                )}
                {!msg.senderId ? (
                  <div className={classes.systemMessage} id={`msg-${msg.id}`}>
                    <span className={classes.systemMessageText}>{msg.body}</span>
                  </div>
                ) : (
                <div className={classes.messageRow} id={`msg-${msg.id}`}>
                  {!grouped ? (
                    <div className={classes.messageAvatarCol} style={{ cursor: msg.sender?.id && msg.sender.id !== user.id ? "pointer" : "default" }}
                      onClick={(e) => { if (msg.sender?.id && msg.sender.id !== user.id) openProfileCard(msg.sender, e); }}>
                      <div style={{ position: "relative", display: "inline-flex" }}>
                        <AvatarEl
                          name={senderName}
                          profileImage={msg.sender?.profileImage}
                          size={36}
                          fontSize={12}
                          className={classes.messageAvatar}
                        />
                        <span
                          className={`${classes.statusDot} ${msg.sender?.online ? classes.statusDotOnline : classes.statusDotOffline}`}
                          style={{ bottom: 0, right: 0, border: `1.5px solid ${dark ? "#0A0A0A" : "#fafafa"}` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={classes.groupedTime}>{formatTime(msg.createdAt)}</div>
                  )}
                  <div className={classes.messageContent}>
                    {!grouped && (
                      <div className={classes.messageHeader}>
                        <span className={classes.messageSender} style={{ cursor: msg.sender?.id && msg.sender.id !== user.id ? "pointer" : "default" }} onClick={(e) => { if (msg.sender?.id && msg.sender.id !== user.id) openProfileCard(msg.sender, e); }}>{senderDisplayName}</span>
                        {msg.sender?.statusEmoji && (
                          <Tooltip title={msg.sender.statusText || ""}>
                            <span style={{ fontSize: 13, cursor: "default" }}>{msg.sender.statusEmoji}</span>
                          </Tooltip>
                        )}
                        {senderCompany && <span className={classes.messageBadge}>{senderCompany}</span>}
                        <span className={classes.messageTime}>{formatTime(msg.createdAt)}</span>
                      </div>
                    )}
                    {msg.isDeleted ? (
                      <div className={classes.messageDeleted}>Mensagem removida</div>
                    ) : (
                      <>
                        {msg.parentMessageId && (() => {
                          const parent = messages.find(m => m.id === msg.parentMessageId);
                          if (!parent) return null;
                          return (
                            <div style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "4px 10px", marginBottom: 4, borderRadius: 6,
                              background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                              borderLeft: `3px solid ${accent}40`,
                              fontSize: 12, color: dark ? "#71717A" : "#A1A1AA",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              const el = document.getElementById(`msg-${parent.id}`);
                              if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.style.background = `${accent}12`; setTimeout(() => { el.style.background = ""; }, 1500); }
                            }}>
                              <span style={{ fontWeight: 600, color: dark ? "#5B7CFF" : "#3D5CFD", fontSize: 11 }}>{getInternalChatDisplayName(parent.sender)}</span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {parent.body?.substring(0, 80) || "Mensagem"}
                              </span>
                            </div>
                          );
                        })()}
                        {(msg.body && msg.body.trim()) || !msg.mediaUrl || editingMessageId === msg.id ? (
                        <div className={classes.messageBody}>
                          {editingMessageId === msg.id ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 2 }}>
                              <TextField
                                autoFocus
                                multiline
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={editingMessageText}
                                onChange={(e) => setEditingMessageText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                                  if (e.key === "Escape") { e.preventDefault(); handleCancelEdit(); }
                                }}
                                inputProps={{ style: { fontSize: 14 } }}
                              />
                              <div style={{ display: "flex", gap: 6 }}>
                                <Button size="small" variant="contained" color="primary" onClick={handleSaveEdit}>Salvar</Button>
                                <Button size="small" onClick={handleCancelEdit}>Cancelar (Esc)</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {parseMessageBody(msg.body, classes.mentionHighlight, classes, allMentionableUsers)}
                              {msg.isEdited && <span className={classes.messageEdited}>(editado)</span>}
                              {msg.isPinned && (
                                <span style={{ fontSize: 11, marginLeft: 6, color: dark ? "#F59E0B" : "#D97706", display: "inline-flex", alignItems: "center", gap: 2 }}>
                                  <Pin size={11} fill="currentColor" /> fixada
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        ) : null}
                        {msg.mediaUrl && (() => {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                          const mediaUrl = `${backendUrl}/public/${msg.mediaUrl}`;
                          const mt = msg.mediaType || "";
                          const isImage = mt.startsWith("image/");
                          const isVideo = mt.startsWith("video/");
                          const isAudio = mt.startsWith("audio/");
                          if (isImage) {
                            return <RotatableImage imageUrl={mediaUrl} className={classes.messageMedia} />;
                          }
                          if (isVideo) {
                            return (
                              <video
                                src={mediaUrl}
                                controls
                                preload="metadata"
                                className={classes.messageVideo}
                              />
                            );
                          }
                          if (isAudio) {
                            return (
                              <audio
                                src={mediaUrl}
                                controls
                                preload="metadata"
                                className={classes.messageAudio}
                              />
                            );
                          }
                          return (
                            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className={classes.messageFile}>
                              <File size={16} />
                              <span style={{ flex: 1 }}>{msg.mediaUrl}</span>
                              <Download size={14} style={{ opacity: 0.5 }} />
                            </a>
                          );
                        })()}
                        {/* removido: contador de respostas (sem painel de tópico) */}
                        {msg.reactions && msg.reactions.length > 0 && (
                          <div className={classes.reactionRow}>
                            {Object.entries(msg.reactions.reduce((acc, r) => { if (!acc[r.emoji]) acc[r.emoji] = []; acc[r.emoji].push(r.userId); return acc; }, {}))
                              .map(([emoji, userIds]) => (
                                <span key={emoji}
                                  className={`${classes.reactionChip} ${userIds.includes(user.id) ? classes.reactionChipActive : ""}`}
                                  onClick={() => handleReaction(msg.id, emoji)}>
                                  {emoji} {userIds.length > 1 ? userIds.length : ""}
                                </span>
                              ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!msg.isDeleted && (
                    <div className={classes.messageHoverActions}>
                      {QUICK_EMOJIS.slice(0, 3).map(emoji => (
                        <IconButton key={emoji} className={classes.hoverActionBtn} size="small" onClick={() => handleReaction(msg.id, emoji)}>
                          <span style={{ fontSize: 15 }}>{emoji}</span>
                        </IconButton>
                      ))}
                      <Tooltip title="Mais reações">
                        <IconButton className={classes.hoverActionBtn} size="small" onClick={(e) => {
                          e.stopPropagation();
                          setReactionAnchorRect(e.currentTarget.getBoundingClientRect());
                          setReactionPickerMsgId(prev => prev === msg.id ? null : msg.id);
                        }}>
                          <Smile size={15} />
                        </IconButton>
                      </Tooltip>
                      <div style={{ width: 1, height: 16, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", margin: "0 2px" }} />
                      <Tooltip title="Responder">
                        <IconButton className={classes.hoverActionBtn} size="small" onClick={() => handleReply(msg)}>
                          <ArrowLeft size={15} style={{ transform: "scaleX(-1) rotate(-45deg)" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mencionar">
                        <IconButton className={classes.hoverActionBtn} size="small" onClick={() => {
                          const name = msg.sender?.name;
                          if (name) {
                            setMessageText(prev => prev + (prev.length > 0 && !prev.endsWith(" ") ? " " : "") + "@" + name + " ");
                            if (!mentionedUsers.find(u => u.id === msg.senderId)) {
                              setMentionedUsers(prev => [...prev, { id: msg.senderId, name }]);
                            }
                            if (textareaRef.current) textareaRef.current.focus();
                          }
                        }}>
                          <AlternateEmailIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={savedMessageIds.has(msg.id) ? "Remover dos salvos" : "Salvar"}>
                        <IconButton className={classes.hoverActionBtn} size="small" onClick={() => handleToggleSave(msg)}>
                          <Bookmark size={15} fill={savedMessageIds.has(msg.id) ? "currentColor" : "none"} />
                        </IconButton>
                      </Tooltip>
                      {(user.profile === "admin" || msg.senderId === user.id) && (
                        <Tooltip title={msg.isPinned ? "Desafixar" : "Fixar no canal"}>
                          <IconButton className={classes.hoverActionBtn} size="small" onClick={() => handleTogglePin(msg)}>
                            <Pin size={15} fill={msg.isPinned ? "currentColor" : "none"} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {msg.senderId === user.id && (
                        <Tooltip title="Editar">
                          <IconButton className={classes.hoverActionBtn} size="small" onClick={() => handleStartEdit(msg)}>
                            <Pencil size={15} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(msg.senderId === user.id || user.profile === "admin") && (
                        <Tooltip title="Excluir">
                          <IconButton className={classes.hoverActionBtn} size="small" onClick={() => handleDeleteMessage(msg.id)}>
                            <Trash2 size={15} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
                )}
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className={classes.inputContainer} style={{ position: "relative" }}>
          {/* Mention autocomplete dropdown */}
          {showMentions && filteredMentionUsers.length > 0 && (
            <div className={classes.mentionDropdown}>
              {filteredMentionUsers.map((u, idx) => {
                return (
                  <div
                    key={u.id}
                    className={`${classes.mentionItem} ${idx === mentionIndex ? classes.mentionItemActive : ""}`}
                    onClick={() => insertMention(u)}
                    onMouseEnter={() => setMentionIndex(idx)}
                  >
                    <AvatarEl name={u.name} profileImage={u.profileImage} size={28} fontSize={10} className={classes.mentionItemAvatar} />
                    <div>
                      <div className={classes.mentionItemName}>{getInternalChatDisplayName(u)}</div>
                      {u.company?.name && groupName && (
                        <div className={classes.mentionItemSub}>{u.company.name}</div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className={classes.mentionHint}>
                <kbd style={{ fontSize: 10, padding: "1px 4px", borderRadius: 3, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, marginRight: 4 }}>↑↓</kbd>
                navegar
                <kbd style={{ fontSize: 10, padding: "1px 4px", borderRadius: 3, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, margin: "0 4px" }}>Tab</kbd>
                selecionar
                <kbd style={{ fontSize: 10, padding: "1px 4px", borderRadius: 3, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, margin: "0 4px" }}>Esc</kbd>
                fechar
              </div>
            </div>
          )}

          {attachedFile && (
            <div className={classes.attachmentPreview} style={{
              border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
              borderBottom: "none", borderRadius: "14px 14px 0 0",
              background: dark ? "rgba(255,255,255,0.02)" : "#fafafa",
            }}>
              {attachedFile.type?.startsWith("image/") ? (
                <img src={URL.createObjectURL(attachedFile)} alt="preview"
                  style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
              ) : (
                <File size={22} style={{ color: accent }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: dark ? "#FAFAFA" : "#09090B" }}>
                  {attachedFile.name || "Imagem colada"}
                </div>
                <div style={{ fontSize: 11, color: dark ? "#71717A" : "#A1A1AA" }}>
                  {(attachedFile.size / 1024).toFixed(0)}KB
                </div>
              </div>
              <IconButton size="small" onClick={() => setAttachedFile(null)} style={{ padding: 4 }}>
                <X size={16} />
              </IconButton>
            </div>
          )}
          {/* Reply preview (estilo WhatsApp) */}
          {replyingTo && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 14px",
              background: dark ? "rgba(91,124,255,0.08)" : "rgba(61,92,253,0.05)",
              border: `1px solid ${dark ? "rgba(91,124,255,0.18)" : "rgba(61,92,253,0.12)"}`,
              borderBottom: "none",
              borderRadius: "14px 14px 0 0",
              fontSize: 12,
              color: dark ? "#A1A1AA" : "#71717A",
            }}>
              <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: dark ? "#5B7CFF" : "#3D5CFD", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: dark ? "#5B7CFF" : "#3D5CFD", marginBottom: 2 }}>
                  Respondendo a {getInternalChatDisplayName(replyingTo.sender)}
                </div>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {replyingTo.body?.substring(0, 120) || (replyingTo.mediaUrl ? "📎 Anexo" : "Mensagem")}
                </div>
              </div>
              <IconButton size="small" onClick={() => setReplyingTo(null)} style={{ padding: 4, color: dark ? "#A1A1AA" : "#71717A" }}>
                <X size={14} />
              </IconButton>
            </div>
          )}

          {(() => {
            const ch = selectedChannel ? (typingUsers[selectedChannel.id] || {}) : {};
            const names = Object.values(ch).map(u => u.name).filter(Boolean);
            if (names.length === 0) return null;
            const text = names.length === 1 ? `${names[0]} está digitando…` :
                         names.length === 2 ? `${names[0]} e ${names[1]} estão digitando…` :
                         `${names.length} pessoas estão digitando…`;
            return (
              <div className={classes.typingIndicator}>
                <span className={classes.typingDots}><span/><span/><span/></span>
                <span>{text}</span>
              </div>
            );
          })()}
          <div className={classes.inputBox} style={(attachedFile || replyingTo) ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}}>
            <textarea
              ref={textareaRef}
              className={classes.inputField}
              placeholder={`Mensagem em ${(selectedChannel.type === "dm" || selectedChannel.type === "group_dm") ? channelDisplayName(selectedChannel) : "#" + (selectedChannel.customName || selectedChannel.name)}...`}
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                detectMention(e.target.value, e.target.selectionStart);
                if (e.target.value.length > 0) emitTyping();
              }}
              onPaste={(e) => {
                const items = e.clipboardData?.items;
                if (!items) return;
                for (const item of items) {
                  if (item.type.startsWith("image/")) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) setAttachedFile(file);
                    return;
                  }
                }
              }}
              onKeyDown={(e) => {
                // Mention dropdown navigation
                if (showMentions && filteredMentionUsers.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setMentionIndex(prev => (prev + 1) % filteredMentionUsers.length);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setMentionIndex(prev => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
                    return;
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    insertMention(filteredMentionUsers[mentionIndex]);
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setShowMentions(false);
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                if (e.key === "b" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleBold(); }
                if (e.key === "i" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleItalic(); }
              }}
              rows={1}
            />
            <div className={classes.inputToolbar}>
              <Tooltip title="Negrito (Ctrl+B)">
                <IconButton className={classes.inputToolbarBtn} size="small" onClick={handleBold}>
                  <FormatBoldIcon style={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italico (Ctrl+I)">
                <IconButton className={classes.inputToolbarBtn} size="small" onClick={handleItalic}>
                  <FormatItalicIcon style={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Lista">
                <IconButton className={classes.inputToolbarBtn} size="small" onClick={handleList}>
                  <FormatListBulletedIcon style={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <div className={classes.inputToolbarDivider} />
              <Tooltip title="Anexar arquivo">
                <IconButton className={classes.inputToolbarBtn} size="small" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip size={17} />
                </IconButton>
              </Tooltip>
              <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileSelect} />
              <Tooltip title="Emoji">
                <IconButton className={classes.inputToolbarBtn} size="small" onClick={(e) => {
                  setEmojiAnchorRect(e.currentTarget.getBoundingClientRect());
                  setShowEmoji(v => !v);
                }} style={showEmoji ? { color: dark ? "#5B7CFF" : "#3D5CFD" } : {}}>
                  <EmojiEmotionsIcon style={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              {showEmoji && (() => {
                const PICKER_W = 352;
                const PICKER_H = 435;
                const r = emojiAnchorRect;
                const left = r ? Math.max(8, Math.min(r.left, window.innerWidth - PICKER_W - 8)) : 8;
                const top = r ? Math.max(8, r.top - PICKER_H - 8) : 200;
                return (
                  <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                    <div style={{ position: "fixed", top, left, zIndex: 1500, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", borderRadius: 12, overflow: "hidden" }}>
                      <Picker
                        data={emojiData}
                        perLine={window.innerWidth < 480 ? 8 : 9}
                        previewPosition="none"
                        skinTonePosition="none"
                        onEmojiSelect={handleAddEmoji}
                        theme={dark ? "dark" : "light"}
                        locale="pt"
                      />
                    </div>
                  </ClickAwayListener>
                );
              })()}
              <IconButton className={classes.sendBtn} size="small" onClick={handleSendMessage} disabled={!messageText.trim() && !attachedFile}>
                <Send size={16} />
              </IconButton>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── MEMBERS ──
  const renderMembersPanel = () => {
    if (!showMembers || !selectedChannel || selectedChannel.type === "dm") return null;
    // Filtro local por nome/email do membro do canal atual.
    const q = memberSearch.trim().toLowerCase();
    const filteredMembers = q
      ? channelMembers.filter(m => {
          const mu = m.user || {};
          const name = (getInternalChatUserName(mu) || mu.name || "").toLowerCase();
          const email = (mu.email || "").toLowerCase();
          return name.includes(q) || email.includes(q);
        })
      : channelMembers;
    return (
      <div className={classes.membersPanel}>
        <div className={classes.membersPanelHeader}>
          <span className={classes.membersPanelTitle}>Membros ({channelMembers.length})</span>
          <IconButton size="small" onClick={() => setShowMembers(false)} style={{ padding: 4 }}>
            <X size={15} />
          </IconButton>
        </div>
        {/* Busca de membros do canal */}
        <div style={{ position: "relative", padding: "10px 16px 8px", flexShrink: 0 }}>
          <Search size={16} style={{ position: "absolute", left: 28, top: "50%", transform: "translateY(-50%)", color: dark ? "#71717A" : "#A1A1AA", pointerEvents: "none" }} />
          <input
            type="text"
            className={classes.dialogSearchInput}
            placeholder="Buscar membro..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            style={{ fontSize: 13, padding: "8px 12px 8px 36px" }}
          />
        </div>
        <div className={classes.membersList}>
          {loadingMembers ? (
            <div style={{ textAlign: "center", padding: 24 }}><CircularProgress size={20} style={{ color: accent }} /></div>
          ) : filteredMembers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: dark ? "#71717A" : "#A1A1AA", fontSize: 13 }}>
              {q ? "Nenhum membro encontrado" : "Sem membros"}
            </div>
          ) : filteredMembers.map(m => {
            const mu = m.user || {};
            const isSelf = mu.id === user.id;
            return (
              <div key={m.userId || m.id} className={classes.memberItem}
                onClick={(e) => { if (!isSelf) openProfileCard(mu, e); }}
                style={{ cursor: isSelf ? "default" : "pointer" }}>
                <div className={classes.memberAvatarWrapper}>
                  <AvatarEl name={mu.name} profileImage={mu.profileImage} size={28} fontSize={10} className={classes.memberAvatar} />
                  <span className={`${classes.memberStatusDot} ${mu.online ? classes.statusDotOnline : classes.statusDotOffline}`} />
                </div>
                <div className={classes.memberInfo}>
                  <div className={classes.memberName}>
                    {getInternalChatDisplayName(mu)}
                    {isSelf && <span style={{ fontSize: 10, opacity: 0.4, marginLeft: 4 }}>(você)</span>}
                  </div>
                  {(m.role === "owner" || m.role === "admin" || (mu.profile === "admin" && m.role !== "admin" && m.role !== "owner") || (groupName && mu.company?.name)) && (
                  <div className={classes.memberRole}>
                    {m.role === "owner" ? "Dono" : m.role === "admin" ? "Admin" : ""}
                    {mu.profile === "admin" && m.role !== "admin" && m.role !== "owner" && "Admin"}
                    {groupName && mu.company?.name && ` \u00B7 ${mu.company.name}`}
                  </div>
                  )}
                </div>
                {user.profile === "admin" && !isSelf && m.role !== "owner" && !selectedChannel.isDefault && (
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveMember(mu.id); }} style={{ opacity: 0.4, padding: 4 }}>
                      <X size={13} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
        {selectedChannel.type === "channel" && !selectedChannel.isDefault && (
          <div className={classes.addMemberSection}>
            <button className={classes.addMemberBtn} onClick={() => { setAddMemberDialogOpen(true); setAddMemberSearch(""); }}>
              <Plus size={15} /> Adicionar membro
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── INFO PANEL (Pinned / Saved / Mentions) ──
  const renderInfoPanel = () => {
    if (!activePanel) return null;
    const title = activePanel === "pinned" ? "Fixadas" : activePanel === "saved" ? "Salvas" : "Mencionado você";
    const icon = activePanel === "pinned" ? <Pin size={15} fill="currentColor" /> : activePanel === "saved" ? <Bookmark size={15} fill="currentColor" /> : <AtSign size={15} />;
    return (
      <div className={classes.threadPanel} style={{ width: 360, minWidth: 360 }}>
        <div className={classes.membersPanelHeader}>
          <span className={classes.membersPanelTitle} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {icon}
            {title}
            {panelMessages.length > 0 && <span style={{ opacity: 0.5, fontWeight: 400, marginLeft: 4 }}>({panelMessages.length})</span>}
          </span>
          <IconButton size="small" onClick={() => setActivePanel(null)} style={{ padding: 4 }}>
            <X size={15} />
          </IconButton>
        </div>
        <div className={classes.threadBody}>
          {loadingPanel ? (
            <div style={{ textAlign: "center", padding: 32 }}><CircularProgress size={20} style={{ color: dark ? "#5B7CFF" : "#3D5CFD" }} /></div>
          ) : panelMessages.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: dark ? "#71717A" : "#A1A1AA", fontSize: 13 }}>
              {activePanel === "pinned" ? "Nenhuma mensagem fixada nesse canal." :
               activePanel === "saved" ? "Você ainda não salvou mensagens." :
               "Sem menções recentes."}
            </div>
          ) : panelMessages.map(m => (
            <div key={m.id} className={classes.threadReply} style={{ borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, cursor: "pointer" }}
              onClick={() => {
                if (m.channelId) {
                  handleSelectChannel(m.channelId);
                  setTimeout(() => {
                    const el = document.getElementById(`msg-${m.id}`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }, 400);
                }
              }}>
              <div style={{ display: "flex", gap: 10 }}>
                <AvatarEl name={m.sender?.name} profileImage={m.sender?.profileImage} size={28} fontSize={10} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span className={classes.messageSender} style={{ fontSize: 13 }}>{getInternalChatDisplayName(m.sender)}</span>
                    {groupName && m.sender?.company?.name && (
                      <span className={classes.companyBadge}>{m.sender.company.name}</span>
                    )}
                    <span className={classes.messageTime}>{formatTime(m.createdAt)}</span>
                  </div>
                  <div className={classes.messageBody} style={{ fontSize: 13 }}>
                    {parseMessageBody(m.body, classes.mentionHighlight, classes, allMentionableUsers)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── THREAD ── (desativado a pedido — usar reply inline estilo WhatsApp)
  const renderThreadPanel = () => {
    return null;
    // eslint-disable-next-line no-unreachable
    if (!threadParentMsg) return null;
    const parent = messages.find(m => m.id === threadParentMsg.id) || threadParentMsg;
    const replies = messages.filter(m => m.parentMessageId === threadParentMsg.id);
    return (
      <div className={classes.threadPanel}>
        <div className={classes.membersPanelHeader}>
          <span className={classes.membersPanelTitle}>Tópico</span>
          <IconButton size="small" onClick={closeThread} style={{ padding: 4 }}>
            <X size={15} />
          </IconButton>
        </div>
        <div className={classes.threadBody}>
          <div className={classes.threadParentMsg}>
            <div style={{ display: "flex", gap: 10 }}>
              <AvatarEl name={parent.sender?.name} profileImage={parent.sender?.profileImage} size={34} fontSize={11} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span className={classes.messageSender}>{getInternalChatDisplayName(parent.sender)}</span>
                  {groupName && parent.sender?.company?.name && (
                    <span className={classes.companyBadge}>{parent.sender.company.name}</span>
                  )}
                  <span className={classes.messageTime}>{formatTime(parent.createdAt)}</span>
                </div>
                <div className={classes.messageBody}>
                  {parseMessageBody(parent.body, classes.mentionHighlight, classes, allMentionableUsers)}
                  {parent.isEdited && <span className={classes.messageEdited}>(editado)</span>}
                </div>
              </div>
            </div>
          </div>
          <div className={classes.threadDivider}>
            <span className={classes.threadDividerText}>
              {replies.length === 0 ? "Sem respostas ainda" : replies.length === 1 ? "1 resposta" : `${replies.length} respostas`}
            </span>
          </div>
          {replies.map(r => (
            <div key={r.id} className={classes.threadReply}>
              <div style={{ display: "flex", gap: 10 }}>
                <AvatarEl name={r.sender?.name} profileImage={r.sender?.profileImage} size={28} fontSize={10} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span className={classes.messageSender} style={{ fontSize: 13 }}>{getInternalChatDisplayName(r.sender)}</span>
                    {groupName && r.sender?.company?.name && (
                      <span className={classes.companyBadge}>{r.sender.company.name}</span>
                    )}
                    <span className={classes.messageTime}>{formatTime(r.createdAt)}</span>
                  </div>
                  <div className={classes.messageBody}>
                    {parseMessageBody(r.body, classes.mentionHighlight, classes, allMentionableUsers)}
                    {r.isEdited && <span className={classes.messageEdited}>(editado)</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={classes.threadInputContainer}>
          <TextField
            multiline
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Responder no tópico..."
            value={threadText}
            onChange={(e) => setThreadText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendThread(); }
              if (e.key === "Escape") { e.preventDefault(); closeThread(); }
            }}
            inputProps={{ style: { fontSize: 13 } }}
          />
          <IconButton onClick={handleSendThread} disabled={!threadText.trim()} size="small">
            <Send size={16} />
          </IconButton>
        </div>
      </div>
    );
  };

  // ── DRAG & DROP HANDLERS ──
  const onDocDragOver = (e) => {
    if (!selectedChannelId) return;
    if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDocDrop = (e) => {
    if (!selectedChannelId) return;
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setAttachedFile(f);
  };
  useEffect(() => {
    window.addEventListener("dragover", onDocDragOver);
    window.addEventListener("drop", onDocDrop);
    const onLeave = (e) => { if (e.clientX === 0 && e.clientY === 0) setDragOver(false); };
    window.addEventListener("dragleave", onLeave);
    return () => {
      window.removeEventListener("dragover", onDocDragOver);
      window.removeEventListener("drop", onDocDrop);
      window.removeEventListener("dragleave", onLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId]);

  // ── RENDER ──
  return (
    <div className={classes.root}>
      {/* Overlay drag-and-drop */}
      {dragOver && selectedChannelId && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
          border: `3px dashed ${dark ? "#5B7CFF" : "#3D5CFD"}`, borderRadius: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none"
        }}>
          <div style={{
            background: dark ? "#18181B" : "#fff", padding: "32px 48px", borderRadius: 16,
            border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)", textAlign: "center"
          }}>
            <Paperclip size={36} style={{ color: dark ? "#5B7CFF" : "#3D5CFD", marginBottom: 12 }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" }}>Solte para enviar</div>
            <div style={{ fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginTop: 4 }}>O arquivo será anexado à mensagem</div>
          </div>
        </div>
      )}
      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: 16, background: dark ? "#18181B" : "#fff" } }}>
        <DialogTitle style={{ fontWeight: 700 }}>Adicionar Membro</DialogTitle>
        <DialogContent>
          <TextField autoFocus placeholder="Buscar usuario..." size="small" variant="outlined" fullWidth
            value={addMemberSearch} onChange={(e) => setAddMemberSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={18} style={{ opacity: 0.4 }} /></InputAdornment> }}
            style={{ marginBottom: 12 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 300, overflowY: "auto" }}>
            {filteredNonMembers.map(u => {
              return (
                <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, cursor: "pointer", transition: "background 0.1s" }}
                  onClick={() => handleAddMember(u.id)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = dark ? "rgba(255,255,255,0.04)" : "#f5f5f5"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                  <AvatarEl name={u.name} profileImage={u.profileImage} size={28} fontSize={10} className={classes.memberAvatar} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{getInternalChatDisplayName(u)}</div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>{u.email}{groupName && u.company?.name && ` \u00B7 ${u.company.name}`}</div>
                  </div>
                </div>
              );
            })}
            {filteredNonMembers.length === 0 && (
              <Typography style={{ fontSize: 13, opacity: 0.5, textAlign: "center", padding: 20 }}>
                {nonMembers.length === 0 ? "Todos os usuarios ja sao membros" : "Nenhum usuario encontrado"}
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions><Button onClick={() => setAddMemberDialogOpen(false)}>Fechar</Button></DialogActions>
      </Dialog>

      {/* Create Channel Dialog */}
      {/* Global Search */}
      <CustomModal open={globalSearchOpen} onClose={() => { setGlobalSearchOpen(false); setGlobalSearchQuery(""); setGlobalSearchResults([]); }} width={720} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Buscar em todos os canais</span>
          <button onClick={() => { setGlobalSearchOpen(false); setGlobalSearchQuery(""); setGlobalSearchResults([]); }} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div className={classes.dialogSearchWrap} style={{ position: "relative" }}>
          <Search size={18} style={{ position: "absolute", left: 32, top: "50%", transform: "translateY(-50%)", color: dark ? "#71717A" : "#A1A1AA", pointerEvents: "none" }} />
          <input
            autoFocus
            type="text"
            className={classes.dialogSearchInput}
            placeholder="Digite pelo menos 2 caracteres..."
            value={globalSearchQuery}
            onChange={(e) => { setGlobalSearchQuery(e.target.value); runGlobalSearch(e.target.value); }}
          />
        </div>
        <div style={{ paddingTop: 0, padding: "0 20px 12px", overflowY: "auto" }}>
          {loadingGlobalSearch ? (
            <div style={{ textAlign: "center", padding: 32 }}><CircularProgress size={20} style={{ color: dark ? "#5B7CFF" : "#3D5CFD" }} /></div>
          ) : globalSearchResults.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: dark ? "#71717A" : "#A1A1AA", fontSize: 13 }}>
              {globalSearchQuery.length >= 2 ? "Sem resultados." : "Digite algo para buscar..."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 420, overflowY: "auto" }}>
              {globalSearchResults.map(m => (
                <div key={m.id} style={{ padding: "10px 12px", borderRadius: 10, cursor: "pointer", borderLeft: `3px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}
                  onClick={() => {
                    setGlobalSearchOpen(false);
                    if (m.channelId) {
                      handleSelectChannel(m.channelId);
                      setTimeout(() => { const el = document.getElementById(`msg-${m.id}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 400);
                    }
                  }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#5B7CFF" : "#3D5CFD" }}>
                      {m.channel?.type === "dm" ? "DM" : "#"} {m.channel?.name || ""}
                    </span>
                    <span style={{ fontSize: 11, opacity: 0.5 }}>·</span>
                    <span className={classes.messageSender} style={{ fontSize: 12 }}>{getInternalChatDisplayName(m.sender)}</span>
                    <span style={{ fontSize: 11, opacity: 0.5 }}>{formatTime(m.createdAt)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: dark ? "#E4E4E7" : "#27272A" }}>
                    {parseMessageBody(m.body, classes.mentionHighlight, classes, allMentionableUsers)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CustomModal>

      {/* Invite Link Dialog */}
      <CustomModal open={!!inviteLink} onClose={() => { setInviteLink(null); setInviteCopied(false); }} width={520} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Convidar para o canal</span>
          <button onClick={() => { setInviteLink(null); setInviteCopied(false); }} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "0 20px 12px" }}>
          <div style={{ fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginBottom: 10 }}>
            Quem abrir esse link e estiver no grupo será adicionado automaticamente no canal.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              readOnly
              type="text"
              className={classes.dialogSearchInput}
              value={inviteLink || ""}
              onClick={(e) => e.target.select()}
              style={{ paddingLeft: 14, fontFamily: "SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}
            />
            <button className={classes.primaryBtn} onClick={handleCopyInvite} style={{ padding: "0 16px", minWidth: 100 }}>
              {inviteCopied ? "✓ Copiado" : "Copiar"}
            </button>
          </div>
          {(user.profile === "admin" || user.super) && (
            <button onClick={handleRotateInvite} style={{
              marginTop: 12, background: "transparent", border: "none", color: dark ? "#A1A1AA" : "#71717A",
              fontSize: 12, cursor: "pointer", textDecoration: "underline",
            }}>
              Invalidar e gerar novo link
            </button>
          )}
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.ghostBtn} onClick={() => { setInviteLink(null); setInviteCopied(false); }}>Fechar</button>
        </div>
      </CustomModal>

      {/* Rename Channel/GDM Dialog */}
      <CustomModal open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} width={460} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Renomear conversa</span>
          <button onClick={() => setRenameDialogOpen(false)} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "0 20px 12px" }}>
          <input
            autoFocus
            type="text"
            className={classes.dialogSearchInput}
            placeholder="Ex: Equipe Comercial, Projeto Samsung..."
            value={renameInput}
            onChange={(e) => setRenameInput(e.target.value.substring(0, 120))}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmitRename(); if (e.key === "Escape") setRenameDialogOpen(false); }}
            style={{ paddingLeft: 12 }}
          />
          <div style={{ fontSize: 11, color: dark ? "#71717A" : "#A1A1AA", marginTop: 8 }}>
            {renameInput.length}/120 caracteres. Deixe vazio pra usar o nome padrão (lista de membros).
          </div>
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.ghostBtn} onClick={() => setRenameDialogOpen(false)}>Cancelar</button>
          <button className={classes.primaryBtn} onClick={handleSubmitRename}>Salvar</button>
        </div>
      </CustomModal>

      {/* Status Custom Dialog */}
      <CustomModal open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} width={460} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Definir status</span>
          <button onClick={() => setStatusDialogOpen(false)} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "0 20px 12px", overflowY: "auto" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
            <TextField
              autoFocus
              variant="outlined"
              size="small"
              className={classes.dialogTextField}
              placeholder="🚀"
              value={statusEmojiInput}
              onChange={(e) => setStatusEmojiInput(e.target.value.substring(0, 8))}
              inputProps={{ style: { fontSize: 22, textAlign: "center", width: 36 } }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              className={classes.dialogTextField}
              placeholder="No que está trabalhando?"
              value={statusTextInput}
              onChange={(e) => setStatusTextInput(e.target.value.substring(0, 100))}
              inputProps={{ maxLength: 100 }}
            />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[
              { e: "💻", t: "Trabalhando" },
              { e: "📞", t: "Em reunião" },
              { e: "🍽️", t: "Almoço" },
              { e: "🌴", t: "De férias" },
              { e: "🤒", t: "Doente" },
              { e: "🚀", t: "Em modo foco" },
              { e: "✈️", t: "Viajando" },
              { e: "🏠", t: "Trabalhando de casa" },
            ].map((s) => (
              <button key={s.t} onClick={() => { setStatusEmojiInput(s.e); setStatusTextInput(s.t); }}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 9px", borderRadius: 16, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, background: "transparent", cursor: "pointer", fontSize: 12, color: dark ? "#E4E4E7" : "#27272A" }}>
                <span>{s.e}</span><span>{s.t}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.ghostBtn} onClick={() => { setStatusEmojiInput(""); setStatusTextInput(""); handleSetStatus(); }}>Limpar</button>
          <button className={classes.ghostBtn} onClick={() => setStatusDialogOpen(false)}>Cancelar</button>
          <button className={classes.primaryBtn} onClick={handleSetStatus}>Salvar</button>
        </div>
      </CustomModal>

      {/* Channel Browser */}
      <CustomModal open={channelBrowserOpen} onClose={() => setChannelBrowserOpen(false)} width={520} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Procurar canais</span>
          <button onClick={() => setChannelBrowserOpen(false)} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div className={classes.dialogListWrap}>
          {loadingBrowser ? (
            <div style={{ textAlign: "center", padding: 24 }}><CircularProgress size={20} style={{ color: dark ? "#5B7CFF" : "#3D5CFD" }} /></div>
          ) : availableChannels.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 8px", color: dark ? "#71717A" : "#A1A1AA", fontSize: 13 }}>
              Você já participa de todos os canais disponíveis.
            </div>
          ) : availableChannels.map(ch => (
            <div key={ch.id} className={classes.dialogListItem}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: dark ? "rgba(91,124,255,0.18)" : "rgba(61,92,253,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: dark ? "#5B7CFF" : "#3D5CFD", fontWeight: 700, fontSize: 16 }}>#</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: dark ? "#FAFAFA" : "#09090B" }}>{ch.name}</div>
                {ch.description && <div style={{ fontSize: 12, color: dark ? "#A1A1AA" : "#71717A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ch.description}</div>}
              </div>
              <button className={classes.primaryBtn} style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => handleJoinChannel(ch.id)}>
                Entrar
              </button>
            </div>
          ))}
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.primaryBtn} onClick={() => setChannelBrowserOpen(false)}>Fechar</button>
        </div>
      </CustomModal>

      <CustomModal open={createChannelOpen} onClose={() => setCreateChannelOpen(false)} width={460} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>Criar Canal</span>
          <button onClick={() => setCreateChannelOpen(false)} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "0 20px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: dark ? "#A1A1AA" : "#71717A", marginBottom: 6 }}>Nome do canal</div>
            <input
              autoFocus
              type="text"
              className={classes.dialogSearchInput}
              placeholder="ex: geral, suporte, dev"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && newChannelName.trim()) handleCreateChannel(); if (e.key === "Escape") setCreateChannelOpen(false); }}
              style={{ paddingLeft: 14 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: dark ? "#A1A1AA" : "#71717A", marginBottom: 6 }}>Descrição (opcional)</div>
            <textarea
              className={classes.dialogSearchInput}
              placeholder="Pra que serve esse canal..."
              value={newChannelDesc}
              onChange={(e) => setNewChannelDesc(e.target.value)}
              rows={3}
              style={{ paddingLeft: 14, paddingTop: 10, paddingBottom: 10, resize: "vertical", fontFamily: "inherit", minHeight: 70 }}
            />
          </div>
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.ghostBtn} onClick={() => setCreateChannelOpen(false)}>Cancelar</button>
          <button className={classes.primaryBtn} onClick={handleCreateChannel} disabled={!newChannelName.trim()}>Criar Canal</button>
        </div>
      </CustomModal>

      {/* New DM / Group DM Dialog */}
      <CustomModal open={newDMOpen} onClose={() => { setNewDMOpen(false); setDmSelected([]); setDmSearch(""); }} width={520} dark={dark}>
        <div className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>
            {dmSelected.length === 0 ? "Nova conversa" : dmSelected.length === 1 ? "Mensagem direta" : `Grupo (${dmSelected.length + 1} pessoas)`}
          </span>
          <button onClick={() => { setNewDMOpen(false); setDmSelected([]); setDmSearch(""); }} className={classes.dialogCloseBtn} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        {/* Chips dos selecionados */}
        {dmSelected.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 20px 8px" }}>
            {dmSelected.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px 4px 4px", borderRadius: 999, background: dark ? "rgba(91,124,255,0.18)" : "rgba(61,92,253,0.12)", border: `1px solid ${dark ? "rgba(91,124,255,0.3)" : "rgba(61,92,253,0.25)"}` }}>
                <AvatarEl name={u.name} profileImage={u.profileImage} size={20} fontSize={9} />
                <span style={{ fontSize: 12, fontWeight: 600, color: dark ? "#5B7CFF" : "#3D5CFD" }}>{u.name}</span>
                <button onClick={() => toggleDmSelect(u)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, color: dark ? "#5B7CFF" : "#3D5CFD", display: "flex" }}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={classes.dialogSearchWrap} style={{ position: "relative" }}>
          <Search size={18} style={{ position: "absolute", left: 32, top: "50%", transform: "translateY(-50%)", color: dark ? "#71717A" : "#A1A1AA", pointerEvents: "none" }} />
          <input
            autoFocus
            type="text"
            className={classes.dialogSearchInput}
            placeholder="Buscar pessoa..."
            value={dmSearch}
            onChange={(e) => setDmSearch(e.target.value)}
          />
        </div>
        <div className={classes.dialogListWrap}>
          {filteredDmUsers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: dark ? "#71717A" : "#A1A1AA", fontSize: 13 }}>
              Nenhum usuário encontrado
            </div>
          ) : filteredDmUsers.map(u => {
            const isSelected = dmSelected.some(x => x.id === u.id);
            return (
              <div key={u.id} className={classes.dialogListItem}
                onClick={() => toggleDmSelect(u)}
                style={{ cursor: "pointer", background: isSelected ? (dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.06)") : undefined }}>
                <div style={{ position: "relative" }}>
                  <AvatarEl name={u.name} profileImage={u.profileImage} size={32} fontSize={11} className={classes.memberAvatar} />
                  {u.online && (
                    <span style={{ position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: "50%", background: "#22C55E", border: `2px solid ${dark ? "#0F0F12" : "#fff"}` }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: dark ? "#FAFAFA" : "#09090B", display: "flex", alignItems: "center", gap: 6 }}>
                    {getInternalChatDisplayName(u)}
                    {u.statusEmoji && <span style={{ fontSize: 13 }}>{u.statusEmoji}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: dark ? "#71717A" : "#A1A1AA" }}>
                    {u.email}
                    {groupName && u.company?.name && ` · ${u.company.name}`}
                  </div>
                </div>
                <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${isSelected ? (dark ? "#5B7CFF" : "#3D5CFD") : (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)")}`, background: isSelected ? (dark ? "#5B7CFF" : "#3D5CFD") : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {isSelected && <span style={{ color: "#fff", fontSize: 14, lineHeight: 1 }}>✓</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className={classes.dialogActions}>
          <button className={classes.ghostBtn} onClick={() => { setNewDMOpen(false); setDmSelected([]); setDmSearch(""); }}>Cancelar</button>
          <button className={classes.primaryBtn} onClick={handleSubmitNewConversation} disabled={dmSelected.length === 0}>
            {dmSelected.length === 0 ? "Selecione pessoa(s)" :
              dmSelected.length === 1 ? "Iniciar conversa" :
              `Criar grupo com ${dmSelected.length + 1} pessoas`}
          </button>
        </div>
      </CustomModal>

      {/* Layout */}
      {isMobile ? (
        showMobileSidebar ? renderSidebar() : (
          <><div className={classes.chatArea}>{renderMessages()}</div>{renderInfoPanel()}{renderMembersPanel()}{renderThreadPanel()}</>
        )
      ) : (
        <>{renderSidebar()}<div className={classes.chatArea}>{renderMessages()}</div>{renderInfoPanel()}{renderMembersPanel()}{renderThreadPanel()}</>
      )}

      {renderChannelMenu()}

      {/* Reaction emoji picker */}
      {reactionPickerMsgId && (() => {
        const PICKER_W = 352;
        const PICKER_H = 435;
        const r = reactionAnchorRect;
        // Preferir abrir abaixo se tiver espaço; senão, acima
        let top, left;
        if (r) {
          const spaceBelow = window.innerHeight - r.bottom;
          const spaceAbove = r.top;
          top = spaceBelow >= PICKER_H + 8 ? r.bottom + 8 : Math.max(8, r.top - PICKER_H - 8);
          left = Math.max(8, Math.min(r.left, window.innerWidth - PICKER_W - 8));
        } else {
          top = 200; left = 200;
        }
        return (
          <ClickAwayListener onClickAway={() => setReactionPickerMsgId(null)}>
            <div style={{
              position: "fixed", top, left, zIndex: 1500,
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
              borderRadius: 12, overflow: "hidden",
            }}>
              <Picker
                data={emojiData}
                perLine={window.innerWidth < 480 ? 8 : 9}
                previewPosition="none"
                skinTonePosition="none"
                onEmojiSelect={handlePickReactionEmoji}
                theme={dark ? "dark" : "light"}
                locale="pt"
              />
            </div>
          </ClickAwayListener>
        );
      })()}

      {/* Profile Card Popover (Mattermost style) */}
      {profileCard && (() => {
        const u = profileCard.user;
        const handle = (u.email || u.name || "").split("@")[0].toLowerCase().replace(/[^a-z0-9.]/g, "");
        const localTime = (() => {
          try {
            const fmt = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
            return fmt.format(new Date()) + " (BRT)";
          } catch { return null; }
        })();
        return (
          <>
            <div onClick={() => setProfileCard(null)} style={{ position: "fixed", inset: 0, zIndex: 1200 }} />
            <div style={{
              position: "fixed",
              top: Math.max(8, Math.min(profileCard.y, window.innerHeight - 340)),
              left: profileCard.x,
              zIndex: 1201,
              width: 248,
              background: dark ? "#1A1A1F" : "#fff",
              borderRadius: 12,
              border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              boxShadow: dark ? "0 12px 36px rgba(0,0,0,0.7)" : "0 12px 36px rgba(0,0,0,0.2)",
              overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}>
              {/* Close button */}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 6px 0" }}>
                <button onClick={() => setProfileCard(null)} style={{ background: "transparent", border: "none", padding: 3, borderRadius: 6, color: dark ? "#A1A1AA" : "#71717A", cursor: "pointer", display: "flex" }}>
                  <X size={14} />
                </button>
              </div>
              {/* Avatar + presence */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: -2 }}>
                <div style={{ position: "relative" }}>
                  <AvatarEl name={u.name} profileImage={u.profileImage} size={52} fontSize={19} className={classes.memberAvatar} />
                  <span style={{
                    position: "absolute", bottom: 2, right: 2,
                    width: 11, height: 11, borderRadius: "50%",
                    background: u.online ? "#22C55E" : (dark ? "#52525B" : "#A1A1AA"),
                    border: `2px solid ${dark ? "#1A1A1F" : "#fff"}`,
                  }} />
                </div>
              </div>
              {/* Última vez visto / Online */}
              <div style={{ textAlign: "center", marginTop: 5, fontSize: 11, color: dark ? "#71717A" : "#A1A1AA" }}>
                {u.online ? "Online agora" : "Offline"}
              </div>
              {/* Nome + status emoji */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4, padding: "0 14px" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", textAlign: "center", lineHeight: 1.2 }}>
                  {u.name}
                </span>
              </div>
              {/* @handle */}
              <div style={{ textAlign: "center", fontSize: 12, color: dark ? "#A1A1AA" : "#71717A", marginTop: 1 }}>
                @{handle}
              </div>
              {/* Cargo / Role */}
              {(u.jobRole?.name || u.profile) && (
                <div style={{ textAlign: "center", fontSize: 12, color: dark ? "#A1A1AA" : "#71717A", marginTop: 2, padding: "0 14px" }}>
                  {u.jobRole?.name || (u.profile === "superadmin" ? "Superadmin" : u.profile === "admin" ? "Admin" : "Atendente")}
                  {groupName && u.company?.name && ` · ${u.company.name}`}
                </div>
              )}
              {/* Divider */}
              <div style={{ height: 1, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", margin: "10px 14px 8px" }} />
              {/* Email */}
              {u.email && (
                <div style={{ padding: "0 14px 6px", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Mail size={14} style={{ color: dark ? "#71717A" : "#A1A1AA", marginTop: 2, flexShrink: 0 }} />
                  <a href={`mailto:${u.email}`} style={{ fontSize: 12, color: dark ? "#5B7CFF" : "#3D5CFD", wordBreak: "break-all", textDecoration: "none" }}>
                    {u.email}
                  </a>
                </div>
              )}
              {/* Horário local */}
              {localTime && (
                <div style={{ padding: "0 14px 6px", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Clock size={14} style={{ color: dark ? "#71717A" : "#A1A1AA", marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: dark ? "#A1A1AA" : "#71717A" }}>Horário local</div>
                    <div style={{ fontSize: 12, color: dark ? "#FAFAFA" : "#09090B" }}>{localTime}</div>
                  </div>
                </div>
              )}
              {/* Status */}
              {(u.statusEmoji || u.statusText) && (
                <div style={{ padding: "0 14px 6px", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Smile size={14} style={{ color: dark ? "#71717A" : "#A1A1AA", marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: dark ? "#A1A1AA" : "#71717A" }}>Status</div>
                    <div style={{ fontSize: 12, color: dark ? "#FAFAFA" : "#09090B", marginTop: 1 }}>
                      {u.statusEmoji && <span style={{ marginRight: 6 }}>{u.statusEmoji}</span>}
                      {u.statusText || ""}
                    </div>
                  </div>
                </div>
              )}
              {/* Footer button */}
              <div style={{ display: "flex", padding: "10px 14px 12px", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, marginTop: 6 }}>
                <button onClick={handleProfileCardDM} className={classes.primaryBtn} style={{ flex: 1, padding: "7px 10px", fontSize: 12.5, fontWeight: 400, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <Send size={13} /> Enviar Mensagem
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Mention notification toast */}
      <Snackbar
        open={!!mentionToast}
        autoHideDuration={5000}
        onClose={() => setMentionToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        className={classes.mentionToast}
        message={mentionToast && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
            onClick={() => {
              if (mentionToast.channelId) handleSelectChannel(mentionToast.channelId);
              setMentionToast(null);
            }}
          >
            <AlternateEmailIcon style={{ fontSize: 18, color: accent }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {mentionToast.senderName} mencionou voce
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                {mentionToast.body}
              </div>
            </div>
          </div>
        )}
      />

      {/* Gate de cargo: user básico sem JobRole não usa Chat Interno.
          Se a company não tem cargos cadastrados, o gate libera (caso contrário
          o user fica em lockout permanente sem ação possível). */}
      <SelectJobRoleDialog
        open={!hasJobRole}
        userId={user?.id}
        onSaved={(updated) => {
          if (updated?.jobRole || updated?.jobRoleId) {
            setHasJobRole(true);
          }
        }}
        onNoRolesAvailable={() => setHasJobRole(true)}
      />
    </div>
  );
};

export default InternalChat;
